/**
 * Trade mode on a whole consolidation (TRADE-CONS step 4b; Pedro 2026-10-30, OPEN_ITEMS TC-S4; design
 * of record `_MEMORY/decisions/2026-10-24_consolidated-trading-optimizer-design.md`). It sits on View
 * and trade portfolio when one consolidation is shown on its own (S4-Q1), beside the single-portfolio
 * trade mode (TradeMode.tsx), with the same table contract:
 *   - one row per security in the shown currency (the agreement's Preferred Ref Ccy by default),
 *     traded in Quantity / Value / % of the CONSOLIDATION, or Target % of it;
 *   - each row expands into one row per portfolio of the consolidation (S4-Q2), each editable: its
 *     Traded Quantity (typing one splits the row by hand), its settlement account (one of THAT
 *     portfolio's cash accounts), its reason to keep a not-approved holding;
 *   - the default split "keeps each security where it lives" (S4-Q3, utils/consolidatedTrade): sales
 *     and buys of a held security pro rata to the holdings; a security no portfolio holds is red until
 *     the portfolio to buy it in is picked;
 *   - each portfolio's share follows its own lot rules and holding (no short sales);
 *   - a cash account a portfolio's trades would overdraw warns on Save (S4-Q4: Continue / Stay),
 *     offering suggested cash transfers (4c);
 *   - 4c (0131): cash transfers within a portfolio or between the consolidation's portfolios (cash
 *     only, F3; one between portfolios confirmed loudly, "crosses banks", S4-Q9), and "Split by
 *     available cash" for a security no portfolio holds (Pedro 2026-10-30, "Both");
 *   - Save → a run: one draft per portfolio, saved together (4a; serial TRC-, each draft its TR-).
 *   - 4d (0132): every save also makes and keeps the run's documents — the client PDF (the run's
 *     currency) and one Excel per custodian bank (a tab per portfolio, in its own currency).
 * Not yet (later increments of step 4): its lifecycle steps (4e).
 */
import { cloneElement, isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Button, Checkbox, Group, Menu, Modal, Popover, SegmentedControl, Select, Stack, Table, Text, TextInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArrowsExchange, IconChevronDown, IconChevronRight, IconInfoCircle, IconRestore, IconTrash,
} from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { portfoliosApi, type ConsolidatedRow, type ConsolidatedView } from '../api/portfolios';
import { modelsApi } from '../api/models';
import type { Consolidation } from '../api/portfolioConsolidations';
import { securitiesApi } from '../api/securities';
import {
  tradesApi, type OptimizeOut, type OptimizerUsed, type PriceSource, type TradeDocIn, type TradeDocLine, type TradeDocOrder,
  type TradeLineIn, type TradeRun, type TradeRunDocIn, type TradeRunDocPart, type TradeRunIn,
} from '../api/trades';
import { OptimizeWindow, type OptimizeRequest, type RunRules } from './ConsolidationOptimize';
import { RunGuide } from './ConsolidationRunGuide';
import { tradeRulesApi } from '../api/tradeRules';
import { CONSOLIDATED_ACCESS, flattenGroups, groupRows, type GroupLens, type GroupLevel } from '../utils/portfolioGrouping';
import { ColumnResizeHandle } from '../components/ColumnResizeHandle';
import { ConsolidationPicker } from '../components/ConsolidationPicker';
import { ModelPicker } from '../components/ModelPicker';
import { PortfolioPicker } from '../components/PortfolioPicker';
import { useDirtyForm } from '../components/DirtyFormProvider';
import { ordered, useColumnOrder, useColumnWidths, useHeadingDrag } from '../components/gridColumnOrder';
import { AdminOverrideSwitch, useAdminOverride } from '../hooks/useAdminOverride';
import { adjustSplit, splitInLots } from '../utils/consolidatedTrade';
import { convert, costUnknown, grossIn, measure, ratesFor, TOTAL, type Ctx } from '../utils/portfolioConsolidation';
import {
  fromQuantity, planConsolidationCash, targetSellsAll, targetTrade, toQuantity, type PlanAccount, type PlanPortfolio, type Side, type Unit,
} from '../utils/tradeMath';
import { addedRow, NEW_PREFIX, SOURCE_TEXT, type Added, type TradeMode } from './TradeMode';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');
const fmt = (v: number | null, digits: number, max = digits) =>
  v == null ? '-' : v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: max });
const amount = (v: number | null) => fmt(v, 2);
const qtyFmt = (v: number | null) => fmt(v, 0, 6);
const pctFmt = (v: number | null) => (v == null ? '-' : `${fmt(v, 2)}%`);
const n = (s: string | null | undefined) => (s == null || s === '' ? null : Number(s));
const clean = (v: number) => String(Number(v.toPrecision(12)));
const EPS = 1e-9;

/** A security row's trade entry: the side and the amount at the consolidated level. */
interface Entry { side: Side | null; amount: string }
const EMPTY: Entry = { side: null, amount: '' };
type Std = 'ref' | 'sec';
/** "row key|portfolio ref" — a portfolio's part of a row. */
const pk = (key: string, p: string) => `${key}|${p}`;
/** TRADE-CONS 4c (Pedro 2026-10-30, "Both"): Buy in → "Split by available cash" — a security no
 *  portfolio holds, shared over the portfolios pro rata to the cash each has after the other trades. */
const CASH_PICK = '__cash__';
/** TC-S5-STEP1 (Pedro 2026-11-01, "Let's try it"): Buy in → "Let the optimizer choose (step 2)" — step 1
 *  is settled for that row; step 2 must then optimize (Keep is greyed while such a row is left). */
const OPT_PICK = '__opt__';
/** TRADE-CONS 4c (0131): cash moved from one account to another — of the same portfolio or of
 *  ANOTHER portfolio of the run. `out` leaves `from` in its currency; `inn` reaches `to` in its. */
interface RunTransfer { fromP: string; from: string; toP: string; to: string; out: number; inn: number }
/** A transfer being edited in the window: accounts as "portfolio|code", Out as typed. */
interface XRow { from: string | null; to: string | null; out: string }
const splitAcc = (k: string) => { const i = k.lastIndexOf('|'); return { p: k.slice(0, i), code: k.slice(i + 1) }; };

/** A portfolio's part of a row's trade. */
interface Share {
  held: number; unitValue: number | null; q: number; note: string | null; value: number;
  price: number | null; priceText: string | null;   // as the line gives it (sent as is)
  priceSource: PriceSource | null; accruedPerUnit: number | null;   // accrued in the portfolio's ref ccy
  notApprovedKept: boolean; settle: string | null;
}
interface RowCalc {
  side: Side | null; held: number; unitValue: number | null; q: number; value: number; notes: string[];
  /** A buy of a security no portfolio holds, with no portfolio picked (S4-Q3). */
  noHome: boolean; manual: boolean; capped: boolean; shares: Record<string, Share>;
  /** 4c: "Split by available cash", but no portfolio has cash to spare. */
  noCash?: boolean;
}

const UNIT_LABEL = (u: Unit, ref: string) => (u === 'quantity' ? 'Quantity' : u === 'value' ? `Value (Ref Ccy ${ref})` : '% of consolidation');

interface TCol { key: string; label: string; tip: string; width: number; num?: boolean; after?: boolean }

export function useConsolidationTradeMode({ c, ctx, refCcy, rows: viewRows, enabled, onLatest, consolidation, openRun, groupBy }: {
  c: ConsolidatedView; ctx: Ctx; refCcy: string; rows: ConsolidatedRow[];
  /** 4d: the documents' Before and after follows the grouping on screen (as TP-PDF for one portfolio). */
  groupBy: { levels: GroupLevel[]; lenses: GroupLens[] };
  /** `trades.draft`, and one consolidation shown on its own. */
  enabled: boolean; onLatest: () => void;
  consolidation: Consolidation | null;
  /** A Trades row asked to open this run (once per request `n`). */
  openRun?: { id: string; n: number } | null;
}): TradeMode {
  const qc = useQueryClient();
  const { registerDirty, confirmDiscardAsync } = useDirtyForm();
  const override = useAdminOverride();
  const [active, setActive] = useState(false);
  const [unit, setUnit] = useState<Unit>('pct');
  const [target, setTarget] = useState(false);
  const [onlyTraded, setOnlyTraded] = useState(false);
  const [std, setStd] = useState<Std>('ref');
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  /** A row split by hand: each portfolio's quantity as typed (S4-Q2). */
  const [manual, setManual] = useState<Record<string, Record<string, string>>>({});
  /** S4-Q3: the portfolio a security no portfolio holds is bought in. */
  const [pick, setPick] = useState<Record<string, string>>({});
  /** A portfolio's settlement account for a row, when changed from the standard choice. */
  const [settle, setSettle] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [added, setAdded] = useState<Added[]>([]);
  // 4c: the run's cash transfers, and the transfer window (its rows; the loud confirm, S4-Q9).
  const [transfers, setTransfers] = useState<RunTransfer[]>([]);
  const [xferOpen, setXferOpen] = useState(false);
  const [xferRows, setXferRows] = useState<XRow[]>([]);
  const [xferChecked, setXferChecked] = useState(false);
  /** 5c: the transfer window was opened by Rebalance to a model (it then says what the choice is). */
  const [xferFromRebalance, setXferFromRebalance] = useState(false);
  // TC-S5-GUIDE (Pedro 2026-11-01, G1–G4): the run's steps — 1 Securities · 2 Split · 3 Cash · 4 Save.
  /** After Rebalance to a model: the plan's own cash transfers (offered at step 3 when its split is kept) … */
  const [planXfers, setPlanXfers] = useState<XRow[]>([]);
  const [planXfersUsed, setPlanXfersUsed] = useState(false);
  /** … and the model's cash per currency (% of the consolidation), checked at step 3. */
  const [modelCash, setModelCash] = useState<{ label: string; pct: Record<string, number> } | null>(null);
  /** Step 2: "Keep suggested securities trades and check cash" chosen (the optimized choice is `optUsed`). */
  const [keptSplit, setKeptSplit] = useState(false);
  /** Step 3: the cash issues accepted as they are (their text at that moment; any change asks again). */
  const [cashAccepted, setCashAccepted] = useState<string | null>(null);
  /** The step whose detail is open (null = the first one not done). */
  const [guideStep, setGuideStep] = useState<number | null>(null);
  /** TC-S5-CLUTTER: the rebalance note's detail opened. */
  const [rebOpen, setRebOpen] = useState(false);
  /** 4c: Rebalance to a model — its cash per currency (% of the consolidation), reached with transfers. */
  const [pendingCash, setPendingCash] = useState<Record<string, number> | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  /** C (Pedro 2026-10-30, "yes to all"): a traded row opens to its portfolios by default; these were closed by hand. */
  const [closed, setClosed] = useState<Set<string>>(new Set());
  /** A (same answer): the security just added to buy, whose portfolio is being asked for. */
  const [askBuyIn, setAskBuyIn] = useState<string | null>(null);
  /** A portfolio's amount as being typed (in the unit), until the field is left; then it shows the trade. */
  const [shareText, setShareText] = useState<Record<string, string>>({});
  /** Step-7 smoke: the cursor goes to the chosen portfolio's amount box ("row|portfolio"). */
  const shareInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const [focusShare, setFocusShare] = useState<string | null>(null);
  useEffect(() => {
    if (!focusShare || askBuyIn) return;   // after the "which portfolio?" window has closed
    const el = shareInputs.current[focusShare];
    if (!el || el.disabled) return;
    const t = window.setTimeout(() => { el.focus(); el.select(); setFocusShare(null); }, 60);
    return () => window.clearTimeout(t);
  });
  const [title, setTitle] = useState('');
  const [runId, setRunId] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<TradeRun | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [rebBusy, setRebBusy] = useState(false);
  const [rebPick, setRebPick] = useState<null | 'portfolio' | 'model' | 'consolidation'>(null);
  const [rebNote, setRebNote] = useState<{ label: string; date: string | null; noIsin: number; problems: string[]; info?: string } | null>(null);
  const [rebReject, setRebReject] = useState<{ label: string; offenders: { name: string; now: number; want: number }[] } | null>(null);
  const [adding, setAdding] = useState(false);
  // 5c: the optimizer — its window; the rows it split (re-optimized on the next run, until split by
  // hand); the transfers between portfolios it suggested (replaced on the next run); what it used.
  const [optOpen, setOptOpen] = useState(false);
  const [optKeys, setOptKeys] = useState<Set<string>>(new Set());
  const [optXfers, setOptXfers] = useState<RunTransfer[]>([]);
  const [optUsed, setOptUsed] = useState<OptimizerUsed | null>(null);
  /** A row split by the optimizer and then changed by hand: the run is no longer "as optimized" —
   *  unless the optimizer traded nothing in that row (5c smoke: removing an unpriced fund it could
   *  not buy cleared "optimized"). */
  //  5c smoke (Pedro 2026-11-01): a run reopened from Trades keeps its "optimized" record but has no
  //  rows of this session's optimizing (`optKeys` empty) — a share typed by hand there must end it
  //  too. So: a share typed by hand (`typed`) always ends it; removing or re-siding a row ends it
  //  when that row traded something.
  const unOpt = (key: string, typed = false) => {
    if (optKeys.has(key)) setOptKeys((s) => { const x = new Set(s); x.delete(key); return x; });
    if (!optUsed) return;
    if (typed || Object.values(manual[key] ?? {}).some((q) => q.trim() !== '' && Number(q) !== 0)) {
      setOptUsed(null);
      setKeptSplit(true);   // TC-S5-GUIDE step 2: the split is now the user's own
    }
  };

  const addedRows = useMemo(() => added.map(addedRow), [added]);
  const rows = useMemo(() => [...addedRows, ...viewRows], [addedRows, viewRows]);
  const latest = c.dates[0];
  const isLatest = c.as_of === latest;

  // The consolidation's portfolios, in its order, with their label and reference currency.
  const members = useMemo(() => (consolidation?.members ?? []).map((m) => {
    const p = c.portfolios.find((x) => x.ref === m.ref);
    return { ref: m.ref, label: p ? `${p.bank_mnemonic ?? 'No bank'} · ${p.portfolio_number}` : m.label,
      ccy: p?.ref_currency ?? 'EUR', bank: m.ref.split(':')[0], hasPositions: !!p?.used_date };
  }), [consolidation, c.portfolios]);
  const refs = useMemo(() => members.map((m) => m.ref), [members]);
  const labelOf = (p: string) => members.find((m) => m.ref === p)?.label ?? p;
  const ccyOf = (p: string) => members.find((m) => m.ref === p)?.ccy ?? 'EUR';

  // The consolidation's gross value in the shown currency (the base of % of consolidation).
  const gross = useMemo(() => {
    let s = 0;
    for (const p of refs) { const g = grossIn(p, ctx); if (g == null) return null; s += g; }
    return refs.length ? s : null;
  }, [refs, ctx]);

  // Each portfolio's cash accounts (a cash row is one account; its portfolio from its line).
  const cash = useMemo(() => {
    const out: { p: string; code: string; ccy: string; key: string }[] = [];
    for (const r of rows) {
      if (!r.is_cash || !r.account_code) continue;
      for (const p of refs) if (r.by_portfolio[p]) out.push({ p, code: r.account_code, ccy: r.currency, key: r.key });
    }
    return out;
  }, [rows, refs]);
  const cashOf = (p: string) => cash.filter((a) => a.p === p);
  const defaultAccount = (p: string, row: ConsolidatedRow): string | null => {
    const ccy = std === 'ref' ? ccyOf(p) : row.currency;
    return cashOf(p).find((a) => a.ccy === ccy)?.code ?? null;
  };
  const settleOf = (key: string, p: string, row: ConsolidatedRow) => settle[pk(key, p)] ?? defaultAccount(p, row);
  /** A cash account's currency ("portfolio|code" parts). */
  const accCcy = (p: string, code: string) => cash.find((a) => a.p === p && a.code === code)?.ccy ?? ccyOf(p);
  /** A transfer's two legs in the shown currency, each at its own custodian's rates (null: no rate). */
  const xOut = (t: RunTransfer) => convert(t.out, accCcy(t.fromP, t.from), refCcy, ratesFor(t.fromP, ctx));
  const xIn = (t: RunTransfer) => convert(t.inn, accCcy(t.toP, t.to), refCcy, ratesFor(t.toP, ctx));

  // ── the trades, row by row and portfolio by portfolio ────────────────────────────────────
  const calc = useMemo(() => {
    const out: Record<string, RowCalc> = {};
    const addedBy = Object.fromEntries(added.map((a) => [`${NEW_PREFIX}${a.sec.id}`, a]));
    // 4c: a row bought "split by available cash" is worked out last, from the cash the others leave.
    const byCash = (r: ConsolidatedRow) => pick[r.key] === CASH_PICK && !manual[r.key];
    const one = (r: ConsolidatedRow, cashW?: Record<string, number>): RowCalc => {
      const a = addedBy[r.key];
      const heldBy: Record<string, number> = {};
      const v0By: Record<string, number | null> = {};
      for (const p of refs) {
        heldBy[p] = a ? 0 : (measure(r, p, 'quantity', ctx) ?? 0);
        v0By[p] = a ? null : measure(r, p, 'value_ref', ctx);
      }
      const held = Object.values(heldBy).reduce((s, h) => s + h, 0);
      const v0 = a ? null : measure(r, TOTAL, 'value_ref', { ...ctx, inView: refs.filter((p) => heldBy[p] > 0) });
      // The value per unit, in the shown currency, of each portfolio: its own line's; for one that
      // does not hold it, the consolidation's (another portfolio's line, the same day); for a
      // security added to buy, its price × factor + accrued interest, at that custodian's rates.
      const consUnit = held > 0 && v0 != null ? v0 / held : null;
      // A holding line to price a portfolio that does not hold the security (its price and accrued).
      const holder = refs.find((p) => heldBy[p] > 0 && r.by_portfolio[p]);
      const shareBase = (p: string) => {
        const rates = ratesFor(p, ctx);
        if (a) {
          const pr = n(a.price);
          const cl = pr != null && pr > 0 ? convert(pr * a.factor, a.sec.currency, refCcy, rates) : null;
          const acc = a.accrued.trim() !== '' ? convert(Number(a.accrued), a.accruedCcy, refCcy, rates) : (a.factor < 1 ? null : 0);
          const accRef = a.accrued.trim() !== '' ? convert(Number(a.accrued), a.accruedCcy, ccyOf(p), rates) : (a.factor < 1 ? null : 0);
          return { uv: cl != null && acc != null ? cl + acc : null, price: pr, text: a.price, source: (a.source ?? 'typed') as PriceSource, accRef };
        }
        const own = r.by_portfolio[p];
        if (heldBy[p] > 0 && own) {
          const v = v0By[p];
          const acc = n(own.accrued_ref);
          return { uv: v != null ? v / heldBy[p] : null, price: n(own.price), text: own.price ?? null, source: 'own' as PriceSource,
            accRef: acc != null ? acc / heldBy[p] : null };
        }
        if (!holder) return { uv: null, price: null, text: null, source: null, accRef: null };
        const h = r.by_portfolio[holder];
        const hAcc = n(h.accrued_ref);
        const accRef = hAcc != null && heldBy[holder] > 0
          ? convert(hAcc / heldBy[holder], h.ref_currency ?? 'EUR', ccyOf(p), rates) : null;
        return { uv: consUnit, price: n(h.price), text: h.price ?? null,
          source: (holder.split(':')[0] === p.split(':')[0] ? 'same_custodian_day' : 'other_custodian_day') as PriceSource, accRef };
      };
      const pickRef = pick[r.key] && pick[r.key] !== CASH_PICK && pick[r.key] !== OPT_PICK ? pick[r.key] : null;
      const unitValue = a ? shareBase(pickRef ?? refs[0] ?? '').uv : consUnit;
      const e = entries[r.key] ?? EMPTY;
      const typedBy = manual[r.key];
      let side: Side | null = target ? null : e.side;
      let raw: Record<string, number> | null = null;
      let lotShares: Record<string, { quantity: number; note: string | null }> | null = null;
      const lots = { minPiece: n(r.min_piece), minIncrement: n(r.min_increment) };
      const notes: string[] = [];
      let capped = false, noHome = false, noCash = false;
      if (typedBy) {
        // Split by hand: the side as it was when the split was typed (C-417: entry or worked out).
        side = e.side;
        raw = Object.fromEntries(refs.map((p) => [p, Math.max(0, n(typedBy[p]) ?? 0)]));
      } else if (e.amount.trim() !== '' && (target || e.side)) {
        let q: number | null = null;
        if (target) {
          const t = targetTrade(Number(e.amount), held, unitValue, gross);
          if (t) {
            side = t.side;
            q = t.side ? t.quantity : null;
            if (side === 'sell' && held > 0 && targetSellsAll(Number(e.amount))) q = held;
            if (side === 'buy' && !r.authorized) { q = null; notes.push('not approved: it can only be reduced, not increased'); }
          } else notes.push('cannot be worked out (no value for this line)');
        } else {
          q = toQuantity(Number(e.amount), unit, unitValue, gross);
          if (q == null) notes.push('cannot be worked out (no value for this line)');
        }
        // TF-CAP100, at the consolidated level: a buy never takes a security above 100% of the consolidation.
        if (q != null && side === 'buy' && unitValue && gross) {
          const maxQ = Math.max(0, gross / unitValue - held);
          if (q > maxQ + EPS) { q = maxQ; capped = true; notes.push('capped: at most 100% of the consolidation'); }
        }
        if (q != null && side) {
          // Smoke 2026-10-30: the whole is held to the lot rules once and kept in whole lots (splitInLots).
          // 4c: "Split by available cash" — the weights are each portfolio's cash, not its holding.
          const split = splitInLots(side, q, cashW ?? heldBy, cashW ? null : pickRef, lots, !target && unit === 'quantity');
          if (!split) {
            if (cashW && side === 'buy') noCash = true;
            else { noHome = side === 'buy'; if (side === 'sell') notes.push('not held: nothing to sell'); }
          }
          else {
            raw = Object.fromEntries(refs.map((p) => [p, split.shares[p]?.quantity ?? 0]));
            lotShares = split.shares;
            if (split.note) notes.push(split.note);
          }
        }
      }
      // A split by hand: each portfolio's typed quantity through its own lot rules and holding.
      const adj = lotShares ?? (raw && side ? adjustSplit(side, raw, heldBy, lots, true) : null);
      const shares: Record<string, Share> = {};
      let q = 0, value = 0;
      for (const p of refs) {
        const b = shareBase(p);
        const sq = adj?.[p]?.quantity ?? 0;
        const sv = b.uv != null ? sq * b.uv : 0;
        // A split by hand names each portfolio's adjustment; the default split's moves are shown on
        // the portfolio's own row (its hover), the row saying only what changed the whole.
        if (!lotShares && adj?.[p]?.note && (raw?.[p] ?? 0) > 0) notes.push(`${labelOf(p)}: ${adj[p].note}`);
        q += sq; value += sv;
        shares[p] = {
          held: heldBy[p], unitValue: b.uv, q: sq, note: adj?.[p]?.note ?? null, value: sv, price: b.price, priceText: b.text, priceSource: b.source,
          accruedPerUnit: b.accRef,
          notApprovedKept: !a && !r.authorized && !!r.security_id && heldBy[p] > 0 && !(side === 'sell' && sq >= heldBy[p] - EPS),
          settle: settleOf(r.key, p, r),
        };
      }
      return { side, held, unitValue, q, value, notes, noHome, noCash, manual: !!typedBy, capped, shares };
    };
    const later: ConsolidatedRow[] = [];
    for (const r of rows) {
      if (r.is_cash || r.is_loan) continue;
      if (byCash(r)) { later.push(r); continue; }
      out[r.key] = one(r);
    }
    if (later.length) {
      // Each portfolio's cash after the other trades and the transfers (shown currency), at least 0.
      const avail: Record<string, number> = Object.fromEntries(refs.map((p) => [p, 0]));
      for (const acc of cash) {
        const row = rows.find((x) => x.key === acc.key);
        avail[acc.p] += (row ? measure(row, acc.p, 'value_ref', ctx) : null) ?? 0;
      }
      for (const k of Object.values(out)) {
        if (!k.side) continue;
        for (const p of refs) { const s = k.shares[p]; if (s && s.q > 0) avail[p] += k.side === 'buy' ? -s.value : s.value; }
      }
      for (const t of transfers) { avail[t.fromP] -= xOut(t) ?? 0; avail[t.toP] += xIn(t) ?? 0; }
      const weights = Object.fromEntries(refs.map((p) => [p, Math.max(0, avail[p])]));
      for (const r of later) out[r.key] = one(r, weights);
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, refs, entries, manual, pick, settle, std, unit, target, ctx, gross, added, refCcy, transfers, cash]);

  // Cash after the trades and the transfers, per account (portfolio + code), in the shown currency.
  const cashMove = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of rows) {
      const k = calc[r.key];
      if (!k?.side) continue;
      for (const p of refs) {
        const s = k.shares[p];
        if (!s || s.q <= 0 || !s.settle) continue;
        const key = pk(p, s.settle);
        m[key] = (m[key] ?? 0) + (k.side === 'buy' ? -s.value : s.value);
      }
    }
    for (const t of transfers) {
      m[pk(t.fromP, t.from)] = (m[pk(t.fromP, t.from)] ?? 0) - (xOut(t) ?? 0);
      m[pk(t.toP, t.to)] = (m[pk(t.toP, t.to)] ?? 0) + (xIn(t) ?? 0);
    }
    return m;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, calc, refs, transfers, ctx, refCcy]);
  /** A row's (or one portfolio's part of it) value after the trades, shown currency. */
  const newValueIn = (r: ConsolidatedRow, p: string): number | null => {
    if (!r.by_portfolio[p] && !r.key.startsWith(NEW_PREFIX) && !(calc[r.key]?.shares[p]?.q)) return r.is_cash || r.is_loan ? null : 0;
    const v0 = r.key.startsWith(NEW_PREFIX) ? 0 : measure(r, p, 'value_ref', ctx);
    if (r.is_cash) return v0 == null ? null : v0 + (cashMove[pk(p, r.account_code ?? '')] ?? 0);
    if (r.is_loan) return v0;
    const k = calc[r.key];
    const s = k?.shares[p];
    if (!k || !s) return v0;
    const dq = k.side ? (k.side === 'buy' ? s.q : -s.q) : 0;
    if (s.unitValue == null) return dq ? null : v0;
    return (s.held + dq) * s.unitValue;
  };
  const newValue = (r: ConsolidatedRow): number | null => {
    let s = 0, any = false;
    for (const p of refs) {
      if (!r.by_portfolio[p] && !(calc[r.key]?.shares[p]?.q)) continue;
      const v = newValueIn(r, p);
      if (v == null) return null;
      s += v; any = true;
    }
    return any ? s : (r.key.startsWith(NEW_PREFIX) ? 0 : null);
  };
  const newGrossOf = useMemo(() => {
    const out: Record<string, number | null> = {};
    for (const p of refs) {
      let s: number | null = 0;
      for (const r of rows) {
        if (r.is_loan || (!r.by_portfolio[p] && !(calc[r.key]?.shares[p]?.q))) continue;
        const v = newValueIn(r, p);
        if (v == null) { s = null; break; }
        s += v;
      }
      out[p] = s;
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, calc, cashMove, refs, ctx]);
  const newGross = Object.values(newGrossOf).some((v) => v == null) ? null
    : Object.values(newGrossOf).reduce<number>((s, v) => s + (v ?? 0), 0);

  // ── entry ────────────────────────────────────────────────────────────────────────────────
  const touch = () => setDirty(true);
  const setEntry = (key: string, patch: Partial<Entry>) => {
    setEntries((prev) => ({ ...prev, [key]: { ...(prev[key] ?? EMPTY), ...patch } }));
    touch();
  };
  const dropManual = (key: string) => setManual((m) => { const x = { ...m }; delete x[key]; return x; });
  const setSide = (row: ConsolidatedRow, side: Side) => {
    const e = entries[row.key] ?? EMPTY;
    // Smoke 2026-10-30 (Pedro: "unselecting Sell leaves the % of consolidation filled"): unticking empties the amount.
    setEntry(row.key, e.side === side ? { side: null, amount: '' } : { side });
    dropManual(row.key);
    unOpt(row.key);
  };
  /** A portfolio's quantity typed by hand: the row becomes split by hand, from the split shown now. */
  const typeShare = (row: ConsolidatedRow, p: string, value: string) => {
    const k = calc[row.key];
    const side = (entries[row.key]?.side ?? null) || k?.side || null;   // C-417
    if (!side) return;
    setManual((m) => {
      const cur = m[row.key] ?? Object.fromEntries(refs.map((x) => [x, k && k.shares[x]?.q ? clean(k.shares[x].q) : '']));
      return { ...m, [row.key]: { ...cur, [p]: value } };
    });
    setEntries((prev) => ({ ...prev, [row.key]: { ...(prev[row.key] ?? EMPTY), side } }));
    unOpt(row.key, true);
    touch();
  };
  const resetSplit = (key: string) => { dropManual(key); unOpt(key); touch(); };
  const changeUnit = (u: Unit) => {
    setEntries((p) => Object.fromEntries(Object.entries(p).map(([key, e]) => {
      const k = calc[key];
      if (!k || e.amount.trim() === '') return [key, e];
      const raw = toQuantity(Number(e.amount), unit, k.unitValue, gross);
      const back = raw == null ? null : fromQuantity(raw, u, k.unitValue, gross);
      return [key, { ...e, amount: back == null ? '' : clean(back) }];
    })));
    setUnit(u); touch();
  };
  const toggleTarget = (on: boolean) => {
    setEntries((p) => Object.fromEntries(Object.entries(p).map(([key, e]) => {
      const k = calc[key];
      if (!k || manual[key]) return [key, e];
      if (on) {
        const row = rows.find((r) => r.key === key);
        const v = k.side && k.q > 0 && row ? newValue(row) : null;
        return [key, { ...e, amount: v != null && gross ? clean((v / gross) * 100) : '' }];
      }
      const back = k.side && k.q > 0 ? fromQuantity(k.q, unit, k.unitValue, gross) : null;
      return [key, { side: k.side ?? null, amount: back == null ? '' : clean(back) }];
    })));
    setTarget(on); touch();
  };
  const applyStd = (s: Std) => { setStd(s); setSettle({}); touch(); };
  useEffect(() => registerDirty('trade-run', active && dirty), [active, dirty, registerDirty]);

  // ── a security no portfolio holds, added to buy (3b-2) ───────────────────────────────────
  const secsQ = useQuery({ queryKey: ['securities', 'list', 'all'], queryFn: () => securitiesApi.list({}), enabled: active });
  const heldIds = useMemo(() => new Set(viewRows.map((r) => r.security_id).filter(Boolean)), [viewRows]);
  const buyable = useMemo(() => (secsQ.data?.items ?? [])
    .filter((s) => s.authorized && !heldIds.has(s.id) && !added.some((a) => a.sec.id === s.id))
    .map((s) => ({ value: s.id, label: [s.isin, s.description, s.bb_ticker, s.figi, s.currency].filter(Boolean).join(' · ') })),
  [secsQ.data, heldIds, added]);
  /** The price is looked up for the portfolio it is bought in (or the first portfolio until one is picked). */
  const addSecurity = async (id: string, at: string | null,
    saved?: { price: string | null; source: PriceSource | null; date: string | null; accruedRef: number | null; ccy: string }) => {
    const sec = (secsQ.data?.items ?? []).find((s) => s.id === id) ?? await securitiesApi.get(id);
    const where = at ?? refs[0];
    if (!sec || !where) return null;
    setAdding(true);
    try {
      const pr = await tradesApi.price(where, sec.id);
      const a: Added = {
        sec, price: saved?.price ?? pr.price ?? '', source: saved ? saved.source : (pr.source as PriceSource | null),
        priceDate: saved ? saved.date : (pr.source ? pr.date : null), factor: Number(pr.factor ?? 1), factorGuessed: pr.factor_guessed,
        accrued: saved ? (saved.accruedRef == null ? '' : clean(saved.accruedRef)) : (pr.accrued_per_unit ?? ''),
        accruedCcy: saved ? saved.ccy : (pr.accrued_currency ?? ccyOf(where)),
      };
      setAdded((prev) => [...prev, a]);
      return a;
    } catch (e) {
      notifications.show({ color: 'red', title: 'Price not found', message: errMsg(e) });
      return null;
    } finally { setAdding(false); }
  };
  const pickToBuy = async (id: string | null) => {
    if (!id) return;
    const a = await addSecurity(id, null);
    if (!a) return;
    const key = `${NEW_PREFIX}${a.sec.id}`;
    setEntries((p) => ({ ...p, [key]: { side: 'buy', amount: '' } }));
    setExpanded((s) => new Set(s).add(key));
    setAskBuyIn(key);   // A: ask at once which portfolio buys it
    touch();
  };
  const removeAdded = (key: string) => {
    unOpt(key);
    setAdded((p) => p.filter((a) => `${NEW_PREFIX}${a.sec.id}` !== key));
    setEntries((p) => { const x = { ...p }; delete x[key]; return x; });
    dropManual(key);
    setPick((p) => { const x = { ...p }; delete x[key]; return x; });
    touch();
  };
  const editAdded = (key: string, patch: Partial<Added>) => {
    setAdded((p) => p.map((a) => (`${NEW_PREFIX}${a.sec.id}` === key ? { ...a, ...patch } : a)));
    touch();
  };
  const choosePick = async (key: string, p: string | null) => {
    setPick((x) => { const y = { ...x }; if (p) y[key] = p; else delete y[key]; return y; });
    touch();
    if (p) setClosed((s) => { const y = new Set(s); y.delete(key); return y; });
    // The cursor waits in the chosen portfolio's box; split by cash, in the row's own amount box.
    if (p && p !== CASH_PICK && p !== OPT_PICK) setFocusShare(pk(key, p));
    if (p === CASH_PICK || p === OPT_PICK) return;   // its price stays the one found for the first portfolio
    // The price of the portfolio it is bought in (a custodian's own price comes first), unless typed.
    const a = added.find((x) => `${NEW_PREFIX}${x.sec.id}` === key);
    if (!a || !p || a.source === 'typed') return;
    try {
      const pr = await tradesApi.price(p, a.sec.id);
      editAdded(key, { price: pr.price ?? a.price, source: pr.source as PriceSource | null, priceDate: pr.source ? pr.date : null });
    } catch { /* keep the price shown */ }
  };
  const searchCell = !active ? null : (
    <Tooltip withArrow multiline w={340} label="Buy a security no portfolio of the consolidation holds: type part of its name, ISIN, BB Ticker or FIGI. Approved securities only. Then pick the portfolio to buy it in.">
      <Select size="xs" placeholder={adding ? 'Finding a price…' : '+ Buy a new security (name, ISIN, BB Ticker, FIGI)'}
        data={buyable} searchable clearable value={null} limit={50} disabled={adding}
        nothingFoundMessage={secsQ.isLoading ? 'Loading…' : 'No Approved security matches (or the consolidation already holds it)'}
        onChange={(v) => void pickToBuy(v)} comboboxProps={{ withinPortal: true }} />
    </Tooltip>
  );

  // ── open / leave / load a run ────────────────────────────────────────────────────────────
  const reset = () => {
    setEntries({}); setManual({}); setPick({}); setSettle({}); setNotes({}); setAdded([]); setExpanded(new Set()); setClosed(new Set());
    setTitle(''); setRunId(null); setLastSaved(null); setDirty(false); setRebNote(null); setTransfers([]);
    setOptKeys(new Set()); setOptXfers([]); setOptUsed(null);
    resetGuide();
  };
  /** TC-S5-GUIDE: the steps start again (a new plan, or no trades). */
  const resetGuide = () => {
    setPlanXfers([]); setPlanXfersUsed(false); setModelCash(null); setKeptSplit(false); setCashAccepted(null); setGuideStep(null);
  };
  const enter = () => { if (!isLatest) onLatest(); reset(); setUnit('pct'); setTarget(false); setActive(true); };
  const leave = async () => { if (dirty && !(await confirmDiscardAsync())) return; reset(); setActive(false); };
  const runsQ = useQuery({ queryKey: ['trades', 'runs', consolidation?.agreement_id ?? ''],
    queryFn: () => tradesApi.runs.list(consolidation!.agreement_id), enabled: active && !!consolidation });
  const runs = (runsQ.data?.items ?? []).filter((r) => r.consolidation_id === consolidation?.id);

  const loadRun = async (id: string | null) => {
    if (dirty && !(await confirmDiscardAsync())) return;
    reset();
    if (!id) return;
    try {
      const run = await tradesApi.runs.get(id);
      const nextEntries: Record<string, Entry> = {};
      const nextManual: Record<string, Record<string, string>> = {};
      const nextSettle: Record<string, string> = {};
      const nextNotes: Record<string, string> = {};
      const nextPick: Record<string, string> = {};
      const newSecs = new Map<string, { p: string; ln: NonNullable<TradeRun['drafts'][number]['lines']>[number] }[]>();
      const nextXfers: RunTransfer[] = [];
      for (const d of run.drafts) {
        for (const ln of d.lines ?? []) {
          if (ln.side === 'transfer') {
            // 4c: within the portfolio, or to another portfolio of the run (`to_portfolio`).
            const o = n(ln.quantity), i = n(ln.to_amount);
            if (ln.settle_account && ln.to_account && o != null && i != null) {
              nextXfers.push({ fromP: d.portfolio, from: ln.settle_account, toP: ln.to_portfolio ?? d.portfolio, to: ln.to_account, out: o, inn: i });
            }
            continue;
          }
          const row = viewRows.find((r) => r.security_id && r.security_id === ln.security_id);
          if (!row) {
            if (ln.security_id && ln.side === 'buy') newSecs.set(ln.security_id, [...(newSecs.get(ln.security_id) ?? []), { p: d.portfolio, ln }]);
            continue;
          }
          if (ln.keep_note) nextNotes[pk(row.key, d.portfolio)] = ln.keep_note;
          if (!ln.side) continue;
          // The saved quantities are what count: shown again as a split by hand.
          nextEntries[row.key] = { side: ln.side as Side, amount: '' };
          // Smoke 2026-10-30: the server's "500.0000000000" shown as 500.
          (nextManual[row.key] ??= {})[d.portfolio] = ln.quantity ? clean(Number(ln.quantity)) : '';
          if (ln.settle_account) nextSettle[pk(row.key, d.portfolio)] = ln.settle_account;
        }
      }
      for (const [sid, lines] of newSecs) {
        const first = lines[0];
        const q = n(first.ln.quantity), acc = n(first.ln.accrued_ref);
        const a = await addSecurity(sid, first.p, { price: first.ln.price ?? null, source: first.ln.price_source ?? null,
          date: first.ln.price_date ?? null, accruedRef: acc != null && q ? acc / q : null, ccy: ccyOf(first.p) });
        if (!a) continue;
        const key = `${NEW_PREFIX}${sid}`;
        nextEntries[key] = { side: 'buy', amount: '' };
        nextPick[key] = first.p;
        for (const { p, ln } of lines) {
          (nextManual[key] ??= {})[p] = ln.quantity ? clean(Number(ln.quantity)) : '';
          if (ln.settle_account) nextSettle[pk(key, p)] = ln.settle_account;
        }
      }
      // Each portfolio missing from a row's split by hand trades nothing in it.
      for (const key of Object.keys(nextManual)) for (const p of refs) nextManual[key][p] ??= '';
      setUnit(run.unit); setTarget(false); setStd((run.drafts[0]?.settle_default === 'sec' ? 'sec' : 'ref'));
      setEntries(nextEntries); setManual(nextManual); setSettle(nextSettle); setNotes(nextNotes); setPick(nextPick); setTransfers(nextXfers);
      setTitle(run.title ?? ''); setRunId(run.id); setLastSaved(run); setDirty(false);
      // 5c: kept as saved while its split stays untouched (a loaded run has no instruction to
      // optimize again: its rows come back split by hand).
      setOptUsed(run.optimizer_used ?? null);
      setKeptSplit(!run.optimizer_used);   // TC-S5-GUIDE: a saved run's split was already decided
    } catch (e) {
      notifications.show({ color: 'red', title: 'Run not opened', message: errMsg(e) });
    }
  };
  const openedRef = useRef<number | null>(null);
  useEffect(() => {
    if (!openRun || !enabled || openedRef.current === openRun.n) return;
    if (!isLatest) { onLatest(); return; }
    openedRef.current = openRun.n;
    setActive(true);
    void loadRun(openRun.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRun?.n, openRun?.id, enabled, isLatest]);

  // ── what must be fixed before saving (rows concerned red; never silently dropped) ────────
  const [blockers, blockedKeys, blockerItems] = useMemo(() => {
    const out: string[] = [];
    const keys = new Set<string>();
    // TC-S5-GUIDE step 1: each issue with its row (to show it). TC-S5-STEP1 (Pedro 2026-11-01): step 1 is
    // done only when every row is settled — a row left to the optimizer (Buy in → "Let the optimizer
    // choose") is settled here, and step 2 must then optimize.
    const items: { key: string | null; msg: string }[] = [];
    const add = (key: string, msg: string, sub?: string) => {
      out.push(msg); keys.add(key); if (sub) keys.add(pk(key, sub)); items.push({ key, msg });
    };
    for (const m of members) {
      if (!m.hasPositions) {
        const msg = `${m.label} has no positions on ${c.as_of}: the consolidation cannot be traded on this date.`;
        out.push(msg); items.push({ key: null, msg });
      }
    }
    for (const r of rows) {
      const k = calc[r.key];
      if (!k) continue;
      const e = entries[r.key];
      const typed = (e?.amount ?? '').trim() !== '';
      if (k.noHome && pick[r.key] === OPT_PICK) { /* settled: left to the optimizer at step 2 */ }
      else if (k.noHome) add(r.key, `${r.name}: no portfolio of the consolidation holds it — choose Buy in: a portfolio, Split by available cash, or Let the optimizer choose`);
      else if (k.noCash) add(r.key, `${r.name}: split by available cash, but no portfolio has cash left after the other trades — choose another Buy in (a portfolio, or Let the optimizer choose)`);
      // As for one portfolio: with Target %, only a target that could not be reached (a note says
      // why) blocks — a security already at its target simply trades nothing.
      else if (typed && k.q <= 0 && !k.manual && (k.notes.length ? (k.side || target) : (k.side && !target))) {
        add(r.key, `${r.name}: nothing to trade (${k.notes.join('; ') || 'amount too small'})`);
      }
      if (k.manual && k.side && k.q <= 0) add(r.key, `${r.name}: split by hand, but no portfolio trades any — type a quantity, or reset the split`);
      if (r.key.startsWith(NEW_PREFIX) && (typed || k.manual) && k.unitValue == null) {
        const a = added.find((x) => `${NEW_PREFIX}${x.sec.id}` === r.key);
        add(r.key, !a || !n(a.price) ? `${r.name}: no price found anywhere — type one, or the trade cannot be accepted`
          : `${r.name}: accrued interest per unit unknown — type it (0 if none)`);
      }
      for (const p of refs) {
        const s = k.shares[p];
        if (!s) continue;
        const who = `${r.name} · ${labelOf(p)}`;
        if (k.side && s.q > 0 && !s.settle) add(r.key, `${who}: choose the cash account to settle it (that portfolio has none in the standard currency)`, p);
        if (k.side && s.q > 0 && s.price == null && !r.key.startsWith(NEW_PREFIX)) add(r.key, `${who}: no price, so no value; the trade cannot be accepted`, p);
        if (s.notApprovedKept && !(notes[pk(r.key, p)] ?? '').trim()) add(r.key, `${who}: not approved and kept — give the reason (open the row)`, p);
      }
    }
    return [out, keys, items] as const;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, calc, entries, added, target, refs, notes, members, c.as_of, pick]);

  // ── save ─────────────────────────────────────────────────────────────────────────────────
  const body = (): TradeRunIn => {
    const runUnit: Unit = target ? 'pct' : unit;
    const portfolios = refs.map((p) => {
      const lines: TradeLineIn[] = [];
      for (const r of rows) {
        const k = calc[r.key];
        if (!k || !r.security_id) continue;
        const s = k.shares[p];
        if (!s) continue;
        const note = (notes[pk(r.key, p)] ?? '').trim() || null;
        if (k.side && s.q > 0) {
          const a = added.find((x) => `${NEW_PREFIX}${x.sec.id}` === r.key);
          const amt = fromQuantity(s.q, runUnit, s.unitValue, gross);
          lines.push({ security_id: r.security_id, side: k.side, amount_entered: amt == null ? null : clean(amt), quantity: clean(s.q),
            price: a ? a.price : s.priceText, price_source: a ? (a.source ?? 'typed') : s.priceSource,
            price_date: a ? (a.priceDate ?? c.as_of) : c.as_of,
            accrued_ref: s.accruedPerUnit != null ? clean(s.accruedPerUnit * s.q) : null,
            settle_account: s.settle, keep_note: s.notApprovedKept ? note : null });
        } else if (s.notApprovedKept && note) {
          lines.push({ security_id: r.security_id, side: null, keep_note: note });
        }
      }
      // 4c (0131): each transfer is a line of the SENDING portfolio's draft; `to_portfolio` names the
      // receiver when it is another portfolio of the run. amount_entered = the amount in its Ref Ccy.
      for (const t of transfers.filter((x) => x.fromP === p)) {
        const refAmt = convert(t.out, accCcy(t.fromP, t.from), ccyOf(p), ratesFor(p, ctx));
        lines.push({ security_id: null, side: 'transfer', settle_account: t.from, to_account: t.to,
          quantity: clean(t.out), to_amount: clean(t.inn), amount_entered: refAmt == null ? null : clean(refAmt),
          to_portfolio: t.toP === p ? null : t.toP });
      }
      return { portfolio: p, settle_default: std, settle_default_account: null, lines };
    });
    return { consolidation_id: consolidation!.id, unit: runUnit, title: title.trim() || null, portfolios,
      optimizer_used: optUsed };
  };
  const overdrawn = cash.map((a) => {
    const row = rows.find((r) => r.key === a.key);
    return { p: a.p, code: a.code, value: row ? newValueIn(row, a.p) : null };
  }).filter((o) => o.value != null && o.value < -0.005 && (cashMove[pk(o.p, o.code)] ?? 0) < 0) as { p: string; code: string; value: number }[];
  // ── TC-S5-GUIDE (Pedro 2026-11-01): the run's steps ───────────────────────────────────────
  // 1 Securities: what blocks a trade (no price, no portfolio to buy in, …), each shown on its row.
  // 2 Split: "Keep suggested securities trades and check cash", or "Optimize further (according to
  //   trade rules)" — open once only what the optimizer itself settles is left at step 1.
  // 3 Cash, on the split chosen: an account below zero or a portfolio below its minimum cash must be
  //   fixed or accepted (G3); after a model, each currency off the model's % is shown, accepted by
  //   the same tick. 4 Save: only when 1 is clear, 2 chosen and 3 clear or accepted.
  const rulesQ = useQuery({ queryKey: ['trade-rules', 'list'], queryFn: () => tradeRulesApi.list(), enabled: active && !!consolidation });
  const myRules = rulesQ.data?.items.find((r) => r.consolidation_id === consolidation?.id) ?? null;
  // TC-S5-CLUTTER: one issue per security — "nothing to trade (no value…)" is the consequence of a
  // missing price on the same row, so it is left out when its cause is listed.
  const guideItems = blockerItems.filter((it) => !(it.key && it.msg.includes(': nothing to trade')
    && blockerItems.some((o) => o !== it && o.key === it.key)));
  /** TC-S5-STEP1: the rows whose Buy in is "Let the optimizer choose", not yet placed by it. */
  const leftToOpt = rows.filter((r) => pick[r.key] === OPT_PICK && calc[r.key]?.noHome).map((r) => r.name);
  const splitState: 'none' | 'kept' | 'optimized' = optUsed ? 'optimized' : keptSplit && !leftToOpt.length ? 'kept' : 'none';
  /** A portfolio's cash after the trades and transfers (shown currency); null when a value is missing. */
  const cashAfter = (p: string): number | null => {
    let s = 0;
    for (const a of cashOf(p)) {
      const row = rows.find((r) => r.key === a.key);
      const v = row ? newValueIn(row, p) : null;
      if (v == null) return null;
      s += v;
    }
    return s;
  };
  const cashHard: string[] = [
    ...overdrawn.map((o) => `${labelOf(o.p)} · ${o.code}: ends at ${amount(o.value)} ${refCcy} (below zero).`),
    ...members.flatMap((m) => {
      const own = Number(myRules?.members.find((x) => x.ref === m.ref)?.min_cash ?? 0);
      if (!(own > 0)) return [];
      const min = convert(own, m.ccy, refCcy, ratesFor(m.ref, ctx));
      const have = cashAfter(m.ref);
      return min != null && have != null && have < min - 0.005
        ? [`${m.label}: cash ends at ${amount(have)} ${refCcy}, below its minimum of ${amount(own)} ${m.ccy} (trade rules).`] : [];
    }),
  ];
  const tolPct = Number(myRules?.tolerance_bps ?? 10) / 100;
  const cashSoft: string[] = modelCash && gross ? [...new Set([...Object.keys(modelCash.pct), ...cash.map((a) => a.ccy)])].flatMap((ccy) => {
    let have = 0;
    for (const a of cash.filter((x) => x.ccy === ccy)) {
      const row = rows.find((r) => r.key === a.key);
      have += (row ? newValueIn(row, a.p) : null) ?? 0;
    }
    const pct = (have / gross) * 100, want = modelCash.pct[ccy] ?? 0;
    return Math.abs(pct - want) > tolPct ? [`Cash ${ccy}: ${pctFmt(pct)} of the consolidation after these trades; the model ${modelCash.label} holds ${pctFmt(want)}.`] : [];
  }) : [];
  const cashSig = JSON.stringify([cashHard, cashSoft]);
  const cashOk = cashHard.length + cashSoft.length === 0 || cashAccepted === cashSig;
  const saveWhy = (!!runId && !!lastSaved?.locked && !override) ? null
    : blockers.length ? `Step 1 first: ${blockers.length} securities issue${blockers.length === 1 ? '' : 's'} to fix (see the steps above the table).`
      : splitState === 'none' ? 'Step 2 first: keep the suggested split, or optimize it further.'
        : !cashOk ? 'Step 3 first: fix the cash issues, or accept them as they are.' : null;
  const askSave = (asNew: boolean) => { if (!saveWhy) void save(asNew); };

  // ── 4c: cash transfers, within a portfolio or between the consolidation's portfolios ─────
  // (S4-Q4: an overdraft offers suggested transfers; S4-Q9: one between two portfolios is confirmed
  // loudly — "check that neither sits in an insurance policy the other is not in" — and says when it
  // crosses banks. F3: cash only.)
  const accLabel = (key: string) => { const a = splitAcc(key); return `${labelOf(a.p)} · ${a.code} (${accCcy(a.p, a.code)})`; };
  const accOptions = cash.map((a) => ({ value: pk(a.p, a.code), label: accLabel(pk(a.p, a.code)) }));
  const round2 = (v: number) => Math.round(v * 100) / 100;
  const outOf = (r: XRow) => Number(r.out.replace(',', '.'));
  /** In = Out at the sending custodian's rate (equal in the same currency); read-only, as for one portfolio. */
  const innOf = (r: XRow): number | null => {
    if (!r.from || !r.to || !(outOf(r) > 0)) return null;
    const f = splitAcc(r.from), t = splitAcc(r.to);
    const fc = accCcy(f.p, f.code), tc = accCcy(t.p, t.code);
    if (fc === tc) return outOf(r);
    const v = convert(outOf(r), fc, tc, ratesFor(f.p, ctx));
    return v == null ? null : round2(v);
  };
  const toTransfer = (r: XRow): RunTransfer | null => {
    const inn = innOf(r);
    if (!r.from || !r.to || r.from === r.to || inn == null) return null;
    const f = splitAcc(r.from), t = splitAcc(r.to);
    return { fromP: f.p, from: f.code, toP: t.p, to: t.code, out: outOf(r), inn };
  };
  const toRow = (t: RunTransfer): XRow => ({ from: pk(t.fromP, t.from), to: pk(t.toP, t.to), out: clean(t.out) });
  const crossesBanks = (t: RunTransfer) => t.fromP.split(':')[0] !== t.toP.split(':')[0];
  /** Transfers between the same two accounts in the same direction join into one. */
  const mergeX = (list: RunTransfer[]) => {
    const by = new Map<string, RunTransfer>();
    for (const t of list) {
      const k = `${pk(t.fromP, t.from)}>${pk(t.toP, t.to)}`, p0 = by.get(k);
      by.set(k, p0 ? { ...p0, out: p0.out + t.out, inn: p0.inn + t.inn } : { ...t });
    }
    return [...by.values()];
  };
  const moveOn = (key: string, t: RunTransfer) => (pk(t.toP, t.to) === key ? (xIn(t) ?? 0) : 0) - (pk(t.fromP, t.from) === key ? (xOut(t) ?? 0) : 0);
  /** An account after the trades and a given set of transfers (shown currency); null: no value. */
  const projected = (key: string, list: RunTransfer[]): number | null => {
    const a = cash.find((x) => pk(x.p, x.code) === key);
    const row = a ? rows.find((r) => r.key === a.key) : undefined;
    const v = a && row ? newValueIn(row, a.p) : null;
    if (v == null) return null;
    return v - transfers.reduce((s, t) => s + moveOn(key, t), 0) + list.reduce((s, t) => s + moveOn(key, t), 0);
  };
  const xferList = xferRows.map(toTransfer).filter((t): t is RunTransfer => t != null);
  const xferCrossing = xferList.filter((t) => t.fromP !== t.toP);
  const xferProblems = xferRows.flatMap((r, i) => (!r.from || !r.to ? [`Transfer ${i + 1}: choose both accounts`]
    : r.from === r.to ? [`Transfer ${i + 1}: from and to are the same account`]
      : !(outOf(r) > 0) ? [`Transfer ${i + 1}: the amount out must be above 0`]
        : innOf(r) == null ? [`Transfer ${i + 1}: no exchange rate between these accounts' currencies on this date`] : []));
  /** TC-S5-CASHPLAN: the consolidation's portfolios for the cash planner — bank, wrapper (trade rules),
   *  value and minimum cash, in the currency shown. */
  const planPfs = (): PlanPortfolio[] => members.map((m) => {
    const rm = myRules?.members.find((x) => x.ref === m.ref);
    const own = Number(rm?.min_cash ?? 0);
    return { p: m.ref, bank: m.bank, wrapper: rm?.wrapper ?? null, value: grossIn(m.ref, ctx) ?? 0,
      minCash: own > 0 ? (convert(own, m.ccy, refCcy, ratesFor(m.ref, ctx)) ?? 0) : 0 };
  });
  /** Planned transfers as rows of the transfer window (each amount in its sending account's currency). */
  const toXRows = (list: { from: string; to: string; amount: number }[]): XRow[] => list.map((s) => {
    const f = splitAcc(s.from);
    const o = convert(s.amount, refCcy, accCcy(f.p, f.code), ratesFor(f.p, ctx));
    return { from: s.from, to: s.to, out: o == null ? '' : String(round2(o)) };
  });
  /** Suggested transfers on top of `base` (TC-S5-CASHPLAN; TC-S5-ONESTEP): within each portfolio, every
   *  account brought to at least zero and — after a model — each currency to the model's % of that
   *  portfolio; only an overdraft or a minimum cash a portfolio cannot meet itself comes from another
   *  (same wrapper only, same bank first, never below that one's minimum cash). */
  const cashPlanRows = (base: RunTransfer[]): XRow[] => {
    const accs = cash.map((a) => ({ code: pk(a.p, a.code), p: a.p, ccy: a.ccy, balance: projected(pk(a.p, a.code), base) ?? 0 }));
    return toXRows(planConsolidationCash(accs, planPfs(), modelCash?.pct ?? null).transfers);
  };
  const suggest = (base: RunTransfer[]): XRow[] => cashPlanRows(base);
  const openTransfers = (rowsIn: XRow[], fromRebalance = false) => {
    setXferRows(rowsIn); setXferChecked(false); setXferFromRebalance(fromRebalance); setXferOpen(true);
  };
  const applyTransfers = () => {
    setTransfers(mergeX(xferList)); touch(); setXferOpen(false);
    if (xferFromRebalance) setPlanXfersUsed(true);
  };
  /** Step 3 "Suggest transfers…": the model plan's own transfers when its split was kept and they
   *  are not applied yet; otherwise transfers covering what is overdrawn. Checked in the window. */
  const usePlanX = keptSplit && !optUsed && planXfers.length > 0 && !planXfersUsed;
  const suggestCash = () => {
    if (usePlanX) openTransfers([...transfers.map(toRow), ...planXfers], true);
    else openTransfers([...transfers.map(toRow), ...suggest(transfers)]);
  };

  // ── 5c: Optimize split (TC-S5) ─────────────────────────────────────────────────────────────
  const sameX = (a: RunTransfer, b: RunTransfer) =>
    a.fromP === b.fromP && a.from === b.from && a.toP === b.toP && a.to === b.to && Math.abs(a.out - b.out) < 0.005;
  /** A row the optimizer splits: an instruction (Target %, or an amount with Buy / Sell), and not
   *  split by hand — or split by the optimizer last time (S5-Q8: rows split by hand stay). */
  const optFree = (r: ConsolidatedRow) => {
    if (r.is_cash || r.is_loan) return false;
    const e = entries[r.key];
    if (!e || e.amount.trim() === '' || !(target || e.side)) return false;
    return !manual[r.key] || optKeys.has(r.key);
  };
  /** The transfers that stay as typed: all, less the ones the optimizer suggested last time. */
  const keptTransfers = () => transfers.filter((t) => !optXfers.some((o) => sameX(o, t)));
  const buildOptimize = (rules: RunRules): OptimizeRequest => {
    const problems: string[] = [];
    const warnings: string[] = [];
    if (!gross) problems.push("The consolidation's value cannot be worked out on this date (a value or a rate is missing).");
    const free = rows.filter(optFree);
    const kept = keptTransfers();
    const optMembers = members.map((m) => {
      let cash0 = 0;
      for (const a of cashOf(m.ref)) {
        const row = rows.find((r) => r.key === a.key);
        const v = row ? measure(row, m.ref, 'value_ref', ctx) : null;
        if (v == null) problems.push(`${m.label} · ${a.code}: no value in ${refCcy} (a rate is missing).`);
        cash0 += v ?? 0;
      }
      // Rows that stay as they are (split by hand; or any row not given to the optimizer) and the
      // transfers typed: their cash is spent / received before the optimizer splits the others.
      for (const r of rows) {
        if (r.is_cash || r.is_loan || free.includes(r)) continue;
        const k = calc[r.key];
        const s = k?.shares[m.ref];
        if (k?.side && s && s.q > 0) cash0 += k.side === 'buy' ? -s.value : s.value;
      }
      for (const t of kept) {
        if (t.fromP === m.ref) cash0 -= xOut(t) ?? 0;
        if (t.toP === m.ref) cash0 += xIn(t) ?? 0;
      }
      const minOwn = rules.members[m.ref]?.min_cash ?? 0;
      const minCash = minOwn ? convert(minOwn, m.ccy, refCcy, ratesFor(m.ref, ctx)) : 0;
      if (minCash == null) problems.push(`${m.label}: its minimum cash (${m.ccy}) cannot be converted to ${refCcy} (no rate).`);
      return { ref: m.ref, label: m.label, bank: m.bank, wrapper: rules.members[m.ref]?.wrapper ?? null,
        cash: Math.round(cash0 * 100) / 100, min_cash: minCash ?? 0 };
    });
    const securities = free.map((r) => {
      const k = calc[r.key];
      const e = entries[r.key];
      const holdings = refs.map((p) => {
        const s = k.shares[p];
        if (s && s.held > 0 && s.unitValue == null) problems.push(`${r.name} · ${labelOf(p)}: no value per unit (no price or rate).`);
        const line = r.by_portfolio[p];
        let gain: number | null = null;
        if (line && s && s.held > 0 && !costUnknown(line)) {
          const clean0 = measure(r, p, 'clean_ref', ctx), cost = measure(r, p, 'total_cost', ctx);
          gain = clean0 != null && cost != null ? (clean0 - cost) / s.held : null;
        }
        return { portfolio: p, held: s?.held ?? 0, unit_value: s?.unitValue ?? null, gain_per_unit: gain };
      });
      let tv: number | null;
      if (target) {
        const pct = Number(e.amount);
        tv = targetSellsAll(pct) ? 0 : gross ? (pct / 100) * gross : null;
      } else {
        const qi = toQuantity(Number(e.amount), unit, k.unitValue, gross);
        const v0 = holdings.reduce((s, h) => s + h.held * (h.unit_value ?? 0), 0);
        tv = qi == null || k.unitValue == null ? null : Math.max(0, v0 + (e.side === 'buy' ? 1 : -1) * qi * k.unitValue);
      }
      if (tv == null) problems.push(`${r.name}: its target cannot be worked out (no value for this line).`);
      const v0 = holdings.reduce((s, h) => s + h.held * (h.unit_value ?? 0), 0);
      if (tv != null && tv > v0 + 0.005 && holdings.every((h) => h.unit_value == null)) {
        warnings.push(`${r.name}: no price in any portfolio, so it cannot be bought — type its price on its row first.`);
      }
      return { key: r.key, name: r.name, approved: r.key.startsWith(NEW_PREFIX) || !!r.authorized,
        increment: n(r.min_increment) || 1, min_piece: n(r.min_piece) || 0, target_value: Math.round((tv ?? 0) * 100) / 100, holdings };
    });
    return { body: { gross: gross ?? 0, tolerance_bps: rules.tolerance_bps, ranking: rules.ranking, members: optMembers, securities },
      problems, warnings, free: free.length };
  };
  /** The portfolio's cash account in its reference currency (else its first): where a transfer between portfolios goes. */
  const refAccount = (p: string) => (cashOf(p).find((a) => a.ccy === ccyOf(p)) ?? cashOf(p)[0])?.code ?? null;
  const applyOptimized = (res: OptimizeOut, used: OptimizerUsed) => {
    const free = rows.filter(optFree);
    const nextManual = { ...manual };
    const nextEntries = { ...entries };
    for (const r of free) {
      const ts = res.trades.filter((t) => t.security === r.key);
      const side = ts[0]?.side ?? null;
      nextManual[r.key] = Object.fromEntries(refs.map((p) => {
        const t = ts.find((x) => x.portfolio === p);
        return [p, t ? clean(t.quantity) : ''];
      }));
      nextEntries[r.key] = { ...(entries[r.key] ?? EMPTY), side };
    }
    setManual(nextManual); setEntries(nextEntries);
    setOptKeys(new Set(free.map((r) => r.key))); setOptUsed(used);
    setExpanded((s) => new Set([...s, ...free.filter((r) => res.trades.some((t) => t.security === r.key)).map((r) => r.key)]));
    const kept = keptTransfers();
    const missing: string[] = [];
    const suggested: RunTransfer[] = [];
    for (const t of res.transfers) {
      const fa = refAccount(t.from), ta = refAccount(t.to);
      if (!fa || !ta) { missing.push(`${labelOf(t.from)} → ${labelOf(t.to)}`); continue; }
      const out = convert(t.amount, refCcy, accCcy(t.from, fa), ratesFor(t.from, ctx));
      const x = out == null ? null : toTransfer({ from: pk(t.from, fa), to: pk(t.to, ta), out: String(round2(out)) });
      if (x) suggested.push(x); else missing.push(`${labelOf(t.from)} → ${labelOf(t.to)}`);
    }
    setTransfers(kept); setOptXfers(suggested); touch(); setOptOpen(false);
    setGuideStep(null);   // TC-S5-GUIDE: on to step 3
    if (missing.length) {
      notifications.show({ color: 'red', autoClose: false, title: 'Transfers not prepared',
        message: `No cash account or rate for: ${missing.join(', ')}. Add them with Cash transfers…, or the cash goes short.` });
    }
    // TC-S5-ONESTEP (Pedro 2026-11-02: "Couldn't it have been done in one step?"): the optimizer keeps each
    // portfolio's cash as a whole; the accounts within it (an overdraft in one currency while another holds
    // cash) are planned once the optimized trades are on screen (the effect below), and open in the same
    // transfer window as the optimizer's own transfers.
    setOptPlan({ kept, suggested });
  };
  const [optPlan, setOptPlan] = useState<{ kept: RunTransfer[]; suggested: RunTransfer[] } | null>(null);
  useEffect(() => {
    if (!optPlan) return;
    const { kept, suggested } = optPlan;
    setOptPlan(null);
    const base = [...kept, ...suggested];
    const extra = cashPlanRows(base);
    const extraX = extra.map(toTransfer).filter((t): t is RunTransfer => t != null);
    setOptXfers([...suggested, ...extraX]);   // replaced together by the next Optimize
    const all = [...suggested, ...extraX];
    // Its transfers between portfolios are checked and confirmed in the transfer window (S4-Q9).
    if (all.length) openTransfers([...kept.map(toRow), ...suggested.map(toRow), ...extra]);
    notifications.show({ color: 'green', message: `Optimized split applied${all.length ? `; check and apply its ${all.length} transfer(s) in the window that opened (cash between portfolios, and within each portfolio's accounts)` : ''}. Saved only with the run.` });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optPlan, calc]);
  // 4c (as M-REB for one portfolio): after Rebalance to a model, each currency's cash across the
  // consolidation is brought to the model's % of the consolidation; the suggested transfers open in
  // the window to be checked and applied (a transfer between portfolios needs the loud confirm).
  // What cannot be done is named in the rebalance note, never skipped silently.
  useEffect(() => {
    if (!pendingCash || rebBusy) return;
    const cashPct = pendingCash;
    setPendingCash(null);
    const problems: string[] = [];
    let info: string | null = null;
    const accounts = cash.map((a) => ({ code: pk(a.p, a.code), p: a.p, ccy: a.ccy, balance: projected(pk(a.p, a.code), []) }));
    if (!gross || accounts.some((a) => a.balance == null) || refs.some((p) => grossIn(p, ctx) == null)) {
      problems.push("The cash after the trades could not be worked out (a value or a rate is missing), so no cash transfer was suggested to reach the model's cash.");
    } else {
      // TC-S5-CASHPLAN (Pedro 2026-11-02): portfolio by portfolio — each brought to the model's cash per
      // currency of its own value, within itself; only an overdraft it cannot cover comes from another.
      const plan = planConsolidationCash(accounts as PlanAccount[], planPfs(), cashPct);
      for (const m of plan.missing) problems.push(`The model holds Cash ${m} (${pctFmt(cashPct[m])}) but no portfolio of the consolidation has a ${m} cash account: that share stays where the trades leave it.`);
      for (const p of plan.unswept) problems.push(`${labelOf(p)} has no cash account in a currency the model holds: its cash stays as the trades leave it.`);
      for (const s of plan.short) problems.push(`${accLabel(s.code)}: ${amount(s.amount)} ${refCcy} still below zero — no portfolio of the same wrapper has cash to spare.`);
      const rowsX = toXRows(plan.transfers);
      // TC-S5-FLOW: not opened by itself — they are part of this plan, opened by "Keep this plan".
      setPlanXfers(rowsX);
      info = rowsX.length
        ? `This plan includes ${rowsX.length} cash transfer(s) to reach the model's cash per currency.`
        : "No cash transfer needed to reach the model's cash.";
    }
    setRebNote((p) => (p ? { ...p, problems: [...p.problems, ...problems], info: info ?? undefined } : p));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCash, rebBusy, calc, cashMove]);
  // ── 4d (0132): the run's documents from the screen's figures, stored on every save (as N3) ──
  /** One portfolio's document body in currency `x`: its saved orders, its Before and after grouped as
   *  on screen, the totals; amounts from the screen's figures (shown currency) at its custodian's rates. */
  const docFor = (d: TradeRun['drafts'][number], x: string): TradeDocIn => {
    const p = d.portfolio;
    const rates = ratesFor(p, ctx);
    const s2 = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? null : clean(v));
    const toX = (v: number | null | undefined) => (v == null ? null : convert(v, refCcy, x, rates));
    const orders: TradeDocOrder[] = [];
    let buy = 0, sell = 0;
    for (const ln of d.lines ?? []) {
      if (ln.side === 'transfer') {
        const amt0 = n(ln.amount_entered);   // in the portfolio's Ref Ccy
        const vx = amt0 == null ? null : convert(amt0, ccyOf(p), x, rates);
        const toP = ln.to_portfolio ?? p;
        orders.push({ security_id: null, side: 'transfer', name: null, isin: null, bb_ticker: null,
          currency: accCcy(p, ln.settle_account ?? ''), quantity: ln.quantity ?? '0', price: null, value_sec: null, accrued_sec: null,
          value_ref: s2(vx), settle_account: ln.settle_account ?? null, to_account: ln.to_account ?? null,
          to_amount: ln.to_amount ?? null, to_currency: accCcy(toP, ln.to_account ?? ''), to_portfolio: ln.to_portfolio ?? null });
        continue;
      }
      if (ln.side !== 'buy' && ln.side !== 'sell') continue;
      const row = rows.find((r) => !r.is_cash && !r.is_loan && r.security_id === ln.security_id);
      const sh = row ? calc[row.key]?.shares[p] : undefined;
      const q = n(ln.quantity);
      const secCcy = ln.currency ?? row?.currency ?? null;
      const shown = sh?.unitValue != null && q != null ? q * sh.unitValue : null;
      const accPort = sh?.accruedPerUnit != null && q != null ? q * sh.accruedPerUnit : null;   // portfolio's Ref Ccy
      const vx = toX(shown);
      if (vx != null) { if (ln.side === 'buy') buy += vx; else sell -= vx; }
      orders.push({ security_id: ln.security_id ?? null, side: ln.side, name: ln.name ?? row?.name ?? null,
        isin: ln.isin ?? row?.isin ?? null, bb_ticker: row?.bb_ticker ?? null, currency: secCcy,
        quantity: ln.quantity ?? '0', price: ln.price ?? null, settle_account: ln.settle_account ?? null,
        value_sec: s2(shown != null && secCcy ? convert(shown, refCcy, secCcy, rates) : null),
        accrued_sec: s2(accPort != null && secCcy ? convert(accPort, ccyOf(p), secCcy, rates) : 0), value_ref: s2(vx) });
    }
    // Before and after: every line of this portfolio (and what it buys), grouped as on screen.
    const mine = rows.filter((r) => !r.is_loan && (r.by_portfolio[p] || (calc[r.key]?.shares[p]?.q ?? 0) > 0));
    const g = groupRows(mine, groupBy.levels, groupBy.lenses, CONSOLIDATED_ACCESS);
    const g0 = toX(grossIn(p, ctx)), g1 = toX(newGrossOf[p]);
    const summary: TradeDocLine[] = flattenGroups(g.groups, g.ungrouped).map(({ row: r, path }) => {
      const sh = calc[r.key]?.shares[p];
      const v0 = toX(r.key.startsWith(NEW_PREFIX) ? 0 : measure(r, p, 'value_ref', ctx));
      const v1 = toX(newValueIn(r, p));
      const k = calc[r.key];
      const dq = k?.side && sh && sh.q > 0 ? (k.side === 'buy' ? sh.q : -sh.q) : 0;
      return { security_id: r.security_id ?? null, is_cash: !!r.is_cash, groups: path, name: r.name, isin: r.isin ?? null,
        qty_before: r.is_cash || !sh ? null : s2(sh.held), value_before: s2(v0), pct_before: s2(v0 != null && g0 ? (v0 / g0) * 100 : null),
        qty_after: r.is_cash || !sh ? null : s2(sh.held + dq), value_after: s2(v1), pct_after: s2(v1 != null && g1 ? (v1 / g1) * 100 : null) };
    });
    return { format: 'pdf', orders, summary, buy_total: s2(buy), sell_total: s2(sell), net_total: s2(buy + sell),
      gross_before: s2(g0), gross_after: s2(g1) };
  };
  const buildRunDocs = (r: TradeRun): TradeRunDocIn | null => {
    if (!r.drafts.some((d) => (d.lines ?? []).some((ln) => ln.side))) return null;
    const parts: TradeRunDocPart[] = r.drafts.map((d) => ({
      portfolio: d.portfolio, ref: docFor(d, ccyOf(d.portfolio)), shown: docFor(d, refCcy),
      // 4c: cash this portfolio receives from another portfolio of the run (a line of the sender's draft).
      incoming: r.drafts.flatMap((s) => (s.lines ?? []).filter((ln) => ln.side === 'transfer' && ln.to_portfolio === d.portfolio)
        .map((ln) => {
          const amt0 = n(ln.to_amount), toCcy = accCcy(d.portfolio, ln.to_account ?? '');
          const vr = amt0 == null ? null : convert(amt0, toCcy, ccyOf(d.portfolio), ratesFor(d.portfolio, ctx));
          return { from_portfolio: s.portfolio, settle_account: ln.settle_account ?? '', to_account: ln.to_account ?? '',
            to_amount: ln.to_amount ?? '0', to_currency: toCcy, value_ref: vr == null ? null : clean(vr) };
        })),
    }));
    // Pedro 2026-10-30 ("A Before and After for the Consolidated positions would be welcome"): every
    // line of the consolidation together, in the run's currency, grouped as on screen.
    const s2 = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? null : clean(v));
    const tradedAnywhere = (r: ConsolidatedRow) => refs.some((p) => (calc[r.key]?.shares[p]?.q ?? 0) > 0);
    const all = rows.filter((r) => !r.is_loan && (refs.some((p) => r.by_portfolio[p]) || tradedAnywhere(r)));
    const g = groupRows(all, groupBy.levels, groupBy.lenses, CONSOLIDATED_ACCESS);
    const summary: TradeDocLine[] = flattenGroups(g.groups, g.ungrouped).map(({ row: r, path }) => {
      const k = calc[r.key];
      const v0 = r.key.startsWith(NEW_PREFIX) ? 0 : measure(r, TOTAL, 'value_ref', { ...ctx, inView: refs });
      const v1 = newValue(r);
      const held = k ? refs.reduce((s, p) => s + (k.shares[p]?.held ?? 0), 0) : null;
      const dq = k?.side ? refs.reduce((s, p) => s + (k.shares[p]?.q ?? 0), 0) * (k.side === 'buy' ? 1 : -1) : 0;
      return { security_id: r.security_id ?? null, is_cash: !!r.is_cash, groups: path, name: r.name, isin: r.isin ?? null,
        qty_before: r.is_cash || held == null ? null : s2(held), value_before: s2(v0),
        pct_before: s2(v0 != null && gross ? (v0 / gross) * 100 : null),
        qty_after: r.is_cash || held == null ? null : s2(held + dq), value_after: s2(v1),
        pct_after: s2(v1 != null && newGross ? (v1 / newGross) * 100 : null) };
    });
    return { ccy: refCcy, parts, consolidated: { summary, gross_before: s2(gross), gross_after: s2(newGross) } };
  };

  const save = async (asNew: boolean) => {
    if (blockers.length || !consolidation) return;
    setSaving(true);
    try {
      let r = runId && !asNew ? await tradesApi.runs.save(runId, body(), override) : await tradesApi.runs.create(body());
      setRunId(r.id); setLastSaved(r); setDirty(false);
      // 4d: the client PDF and each bank's Excel, made from the screen's figures and kept with the run.
      const docs = buildRunDocs(r);
      if (docs) {
        try {
          r = { ...r, ...(await tradesApi.runs.storeDocuments(r.id, docs, override)), drafts: r.drafts };
          setLastSaved(r);
        } catch (e) {
          notifications.show({ color: 'orange', title: 'Run saved; its PDF / Excel were not stored',
            message: `${errMsg(e)} Save again to store them.`, autoClose: false });
        }
      }
      void qc.invalidateQueries({ queryKey: ['trades'] });
      const orders = r.drafts.reduce((s, d) => s + d.line_count, 0);
      notifications.show({ color: 'green', title: 'Run saved',
        message: `${r.serial} · ${r.title ?? 'Untitled'} · ${orders} order${orders === 1 ? '' : 's'} over ${r.drafts.length} portfolios (${r.drafts.map((d) => d.serial).join(', ')}). The portfolios are unchanged.${r.files?.length ? ` Documents made: the client PDF and ${r.files.length - 1} bank Excel (Trades).` : ''}` });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Run not saved', message: errMsg(e), autoClose: false });
    } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!runId) return;
    try {
      await tradesApi.runs.remove(runId, override);
      setConfirmDelete(false);
      const serial = lastSaved?.serial;
      reset();
      void qc.invalidateQueries({ queryKey: ['trades'] });
      notifications.show({ color: 'green', title: 'Run deleted', message: `${serial ?? 'The run'} and its drafts were deleted. The portfolios are unchanged.` });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Run not deleted', message: errMsg(e), autoClose: false });
    }
  };

  // ── Rebalance to… on the consolidated weights ────────────────────────────────────────────
  const weightNow = (sid: string) => {
    if (!gross) return 0;
    return viewRows.reduce((s, r) => (r.is_cash || r.is_loan || r.security_id !== sid ? s
      : s + ((measure(r, TOTAL, 'value_ref', { ...ctx, inView: refs }) ?? 0) / gross) * 100), 0);
  };
  const applyRebalance = async (label: string, pctBy: Record<string, number>,
    meta: Record<string, { name: string; authorized: boolean }>, info: { date: string | null; noIsin: number }, extra: string[] = []) => {
    // Pedro 2026-10-23, kept for a consolidation: a target holding more of a not-approved security
    // than the consolidation does is refused as a whole.
    const offenders = Object.entries(pctBy).filter(([sid]) => !meta[sid].authorized)
      .map(([sid, want]) => ({ name: meta[sid].name, now: weightNow(sid), want })).filter((o) => o.want > o.now + 0.005);
    if (offenders.length) { setRebReject({ label, offenders }); return false; }
    const next: Record<string, Entry> = {};
    for (const r of viewRows) {
      if (r.is_cash || r.is_loan || !r.security_id) continue;
      next[r.key] = { side: null, amount: clean(pctBy[r.security_id] ?? 0) };
    }
    setAdded([]); setManual({}); setPick({});
    setOptKeys(new Set()); setOptXfers([]); setOptUsed(null);   // 5c: the trades are replaced, no longer optimized
    const problems = [...extra];
    const fresh: string[] = [];
    for (const [sid, pct] of Object.entries(pctBy)) {
      if (heldIds.has(sid)) continue;
      const a = await addSecurity(sid, null);
      if (a) { next[`${NEW_PREFIX}${sid}`] = { side: null, amount: clean(pct) }; fresh.push(`${NEW_PREFIX}${sid}`); }
      else problems.push(`${meta[sid].name} (${pctFmt(pct)}) was not added: no price was found. Add it with "+ Buy a new security" and type its price.`);
    }
    // The securities still without a portfolio are counted live in the note (smoke 2026-11-01: a fixed
    // sentence stayed red after every one had its portfolio).
    if (fresh.length) setExpanded((s) => new Set([...s, ...fresh]));
    setEntries(next); setTarget(true); touch();
    setRebNote({ label, date: info.date, noIsin: info.noIsin, problems }); setRebOpen(false);
    resetGuide();   // TC-S5-GUIDE: a new plan, its steps again
    return true;
  };
  const rebalanceToPortfolio = async (targetRef: string | null) => {
    if (!targetRef) return;
    setRebBusy(true);
    try {
      const t = await portfoliosApi.consolidated([targetRef], {});
      const pctBy: Record<string, number> = {};
      const meta: Record<string, { name: string; authorized: boolean }> = {};
      let noIsin = 0;
      for (const r of t.rows) {
        if (r.is_cash || r.is_loan) continue;
        if (!r.security_id) { noIsin++; continue; }
        const p = n(r.by_portfolio[targetRef]?.pct);
        if (p == null || p <= 0) continue;
        pctBy[r.security_id] = (pctBy[r.security_id] ?? 0) + p;
        meta[r.security_id] = { name: r.name, authorized: !!r.authorized };
      }
      const tp = t.portfolios.find((p) => p.ref === targetRef);
      await applyRebalance(tp ? `${tp.bank_mnemonic ?? 'No bank'} · ${tp.portfolio_number}` : targetRef, pctBy, meta, { date: t.as_of, noIsin });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Rebalance not done', message: errMsg(e), autoClose: false });
    } finally { setRebBusy(false); }
  };
  const rebalanceToConsolidation = async (cons: Consolidation) => {
    setRebBusy(true);
    try {
      const t = await portfoliosApi.consolidated(cons.members.map((m) => m.ref), {});
      const missing: string[] = [];
      let grossEur = 0;
      for (const p of t.portfolios) {
        const g = n(p.gross_value_eur ?? null);
        if (p.used_date == null) missing.push(`${p.bank_mnemonic ?? 'No bank'} · ${p.portfolio_number} has no positions on ${t.as_of}`);
        else if (g == null) missing.push(`${p.bank_mnemonic ?? 'No bank'} · ${p.portfolio_number}: its total has no exchange rate to EUR`);
        else grossEur += g;
      }
      const valueBy: Record<string, number> = {};
      const meta: Record<string, { name: string; authorized: boolean }> = {};
      let noIsin = 0;
      for (const r of t.rows) {
        if (r.is_cash || r.is_loan) continue;
        if (!r.security_id) { noIsin++; continue; }
        for (const [ref, line] of Object.entries(r.by_portfolio)) {
          const v = n(line.value_eur);
          if (v == null) { missing.push(`${r.name} in ${ref.split(':')[1] ?? ref}: no exchange rate to EUR`); continue; }
          valueBy[r.security_id] = (valueBy[r.security_id] ?? 0) + v;
        }
        meta[r.security_id] = { name: r.name, authorized: !!r.authorized };
      }
      if (missing.length || !grossEur) {
        notifications.show({ color: 'red', title: 'Rebalance not done', autoClose: false,
          message: `Consolidation ${cons.name} cannot be copied: ${missing.length ? missing.join('; ') : 'its total is zero'}.` });
        return;
      }
      const pctBy: Record<string, number> = {};
      for (const [sid, v] of Object.entries(valueBy)) if (v > 0) pctBy[sid] = (v / grossEur) * 100;
      await applyRebalance(`consolidation ${cons.name}`, pctBy, meta, { date: t.as_of, noIsin });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Rebalance not done', message: errMsg(e), autoClose: false });
    } finally { setRebBusy(false); }
  };
  const rebalanceToModel = async (modelId: string, modelName: string) => {
    setRebBusy(true);
    try {
      const w = await modelsApi.weights(modelId);
      const pctBy: Record<string, number> = {};
      const meta: Record<string, { name: string; authorized: boolean }> = {};
      const cashBy: Record<string, number> = {};
      for (const r of w.rows) {
        const p = Number(r.weight_pct);
        if (!(p > 0)) continue;
        if (r.kind === 'cash') { if (r.currency) cashBy[r.currency] = (cashBy[r.currency] ?? 0) + p; continue; }
        if (!r.security_id) continue;
        pctBy[r.security_id] = (pctBy[r.security_id] ?? 0) + p;
        meta[r.security_id] = { name: r.name ?? r.isin ?? '?', authorized: !!r.approved };
      }
      setTransfers([]);
      const ok = await applyRebalance(`model ${modelName}`, pctBy, meta, { date: null, noIsin: 0 });
      if (ok) {
        // 4c: the model's cash per currency, reached with suggested transfers once the trades are on screen.
        if (Object.keys(cashBy).length) setPendingCash(cashBy);
        setModelCash({ label: modelName, pct: cashBy });   // TC-S5-GUIDE step 3 checks it (0 % for a currency it does not hold)
        if (!title.trim()) setTitle(`Rebalance to model ${modelName}`);
      }
    } catch (e) {
      notifications.show({ color: 'red', title: 'Rebalance not done', message: errMsg(e), autoClose: false });
    } finally { setRebBusy(false); }
  };

  // ── columns ──────────────────────────────────────────────────────────────────────────────
  const R = `Ref Ccy ${refCcy}`;
  const baseTcols: TCol[] = active ? [
    { key: 'split', label: 'By portfolio', width: 64, tip: "Open the row to see and change each portfolio's part of the trade." },
    { key: 'buy', label: 'Buy', width: 50, tip: 'Tick to buy more of this security (Approved securities only).' },
    { key: 'sell', label: 'Sell', width: 50, tip: 'Tick to sell (never more than a portfolio holds).' },
    { key: 'amount', label: target ? 'Target % after trade' : UNIT_LABEL(unit, refCcy), width: 110, num: true,
      tip: `How much to buy or sell for the whole consolidation, in ${UNIT_LABEL(unit, refCcy)}. It is split over the portfolios where the security is held (pro rata), and each portfolio's part is then checked against the minimum lot and increment and what it holds. On a portfolio's row: its part, in the same unit (so the parts add up to the security's row).` },
    { key: 'price', label: 'Trade price (Sec Ccy)', width: 96, num: true,
      tip: "The price the trade is valued at, in the security's currency: each portfolio's own line; for a portfolio that does not hold it, another portfolio's line of the same day; for a new security, the first price found for the portfolio it is bought in, or one you type (shaded yellow, with its source)." },
    { key: 'accrued_unit', label: 'Accrued interest / unit', width: 120, num: true,
      tip: "Accrued interest per unit, added to the trade's value, in the portfolio's reference currency (open the row). For a new bond: borrowed from where its price came from, or typed." },
    { key: 'settle', label: 'Settle in / Buy in', width: 150,
      tip: "Each portfolio's trade settles in one of that portfolio's cash accounts, pre-filled from the standard choice above (open the row to change one). For a security no portfolio holds: the portfolio to buy it in." },
    { key: 'keep', label: 'Reason to keep (not approved)', width: 170,
      tip: 'A security not approved in Securities may only be sold. Each portfolio keeping it (all or part) needs a reason, saved with its draft (open the row).' },
    { key: 'traded', label: unit === 'quantity' && !target ? 'Traded (Quantity)' : unit === 'value' && !target ? `Traded (${R})`
      : "Traded (%: of consolidation; a portfolio's row: of that portfolio)", width: 130, num: true, after: true,
      tip: "The trade in the unit: purchases positive, sales negative, after the lot rules. In %: of the consolidation on a security's row, of its own portfolio on a portfolio's row." },
    { key: 'traded_qty', label: 'Traded Quantity', width: 110, num: true, after: true,
      tip: "The trade in quantity: purchases positive, sales negative, after the lot rules. (A portfolio's part is typed in the orange amount column.)" },
    { key: 'traded_value', label: `Traded value (${R})`, width: 110, num: true, after: true,
      tip: `The trade's value in ${refCcy}, accrued interest included: purchases positive, sales negative.` },
    { key: 'new_qty', label: 'New Quantity', width: 100, num: true, after: true, tip: 'The quantity after the trades.' },
    { key: 'new_value', label: `New Value (${R})`, width: 110, num: true, after: true,
      tip: `The value after the trades, in ${refCcy}; a cash account's value after the trades settled in it.` },
    { key: 'new_pct', label: 'New % of portfolio', width: 90, num: true, after: true,
      tip: "On a portfolio's row (and a cash account's): its share of THAT portfolio's gross value after the trades. Blank on a security's row, which spans several portfolios: see New % of consolidation." },
    // Pedro 2026-10-30 ("Yes, build (1) and (2)"): every row's share of the whole consolidation after
    // the trades — a security's portfolio rows add up to its own; all portfolio rows add up to 100%.
    { key: 'new_pct_cons', label: 'New % of consolidation', width: 110, num: true, after: true,
      tip: "The share of the consolidation's gross value after the trades (loans left out): on a security's row its whole holding, on a portfolio's row that portfolio's part. A security's portfolio rows add up to its row; every portfolio row and cash account together add up to 100%." },
  ] : [];
  const tOrder = useColumnOrder('tradesRun.columnOrder', baseTcols.map((x) => x.key));
  const tWidths = useColumnWidths('tradesRun.columnWidths');
  const tDrag = useHeadingDrag(tOrder.place);
  const tcols: TCol[] = ordered(baseTcols, tOrder.order).map((x) => ({ ...x, width: tWidths.widthOf(x.key, x.width) }));
  const width = tcols.reduce((s, x) => s + x.width, 0);
  const cellStyle = (x: TCol, bg: string, bold = false): React.CSSProperties => ({
    width: x.width, minWidth: x.width, maxWidth: x.width, padding: '2px 6px', boxSizing: 'border-box', background: bg,
    borderBottom: '1px solid var(--mantine-color-gray-3)', borderRight: '1px solid var(--mantine-color-gray-2)',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13,
    textAlign: x.num ? 'right' : 'left', fontWeight: bold ? 600 : undefined,
  });
  const headBg = (x: TCol) => (x.after ? 'var(--mantine-color-gray-1)' : 'var(--mantine-color-orange-1)');
  const headCells = (rowSpan: number, top: number) => tcols.map((x) => (
    <th key={`t:${x.key}`} rowSpan={rowSpan} {...tDrag.props(x.key)}
      style={{ ...cellStyle(x, headBg(x), true), whiteSpace: 'normal', lineHeight: 1.2, position: 'sticky', top, zIndex: 3,
        verticalAlign: 'bottom', ...tDrag.marker(x.key) }}>
      <Tooltip label={`${x.tip} Drag to move the column; drag its right edge to resize.`} multiline w={300} withArrow openDelay={300}>
        <span style={{ display: 'block' }}>{x.label}</span>
      </Tooltip>
      <ColumnResizeHandle width={x.width} onResize={(w) => tWidths.setWidth(x.key, w)} />
    </th>
  ));
  const inputStyle: React.CSSProperties = { width: '100%', textAlign: 'right', font: 'inherit',
    border: '1px solid var(--mantine-color-gray-4)', borderRadius: 4, padding: '1px 4px' };
  const inUnit = (q: number, uv: number | null) => fromQuantity(q, target ? 'pct' : unit, uv, gross);
  const unitFmt = (v: number | null) => ((target || unit === 'pct') ? pctFmt(v) : unit === 'quantity' ? qtyFmt(v) : amount(v));
  const signed = (side: Side | null, v: number) => (side === 'sell' ? -v : v);

  /** TC-S5-GUIDE step 1 "Show": the row's first trade cell carries an id to scroll to. */
  const rowCells = (row: ConsolidatedRow, bg: string): React.ReactNode[] => {
    const cells = rowCellsOf(row, bg);
    const first = cells[0];
    return isValidElement(first) ? [cloneElement(first as React.ReactElement<{ id?: string }>, { id: `trade-row-${row.key}` }), ...cells.slice(1)] : cells;
  };
  const rowCellsOf = (row: ConsolidatedRow, bg: string): React.ReactNode[] => tcols.map((x) => {
    const k = calc[row.key];
    const e = entries[row.key] ?? EMPTY;
    const st = cellStyle(x, bg);
    const blank = <td key={`t:${x.key}`} style={st} />;
    if (row.is_loan) return blank;
    if (row.is_cash) {
      const p = refs.find((r) => row.by_portfolio[r]);
      const move = p ? cashMove[pk(p, row.account_code ?? '')] : undefined;
      // 4c (as TF-XFER3 for one portfolio): a cash row has a Sell tick only — cash LEAVES this account;
      // it opens the transfer window with this account as From. A receiving account shows "in".
      const accKey = p ? pk(p, row.account_code ?? '') : '';
      if (x.key === 'buy') {
        const senders = transfers.filter((t) => pk(t.toP, t.to) === accKey);
        if (!senders.length) return blank;
        return (
          <td key={`t:${x.key}`} style={{ ...st, textAlign: 'center' }}
            title={`Receives cash from ${senders.map((t) => accLabel(pk(t.fromP, t.from))).join(', ')}: change it from that account's Sell tick.`}>
            <Text span size="xs" c="dimmed">in</Text>
          </td>);
      }
      if (x.key === 'sell') {
        const mine = transfers.filter((t) => pk(t.fromP, t.from) === accKey);
        return (
          <td key={`t:${x.key}`} style={{ ...st, textAlign: 'center' }}
            title={mine.length ? `${row.account_code} sends cash to another account: click to change or remove it` : `Transfer cash from ${row.account_code} to another account of this portfolio or of another portfolio of the consolidation`}>
            <Checkbox size="xs" checked={mine.length > 0} aria-label={`Transfer from ${row.account_code}`}
              onChange={() => openTransfers([...transfers.map(toRow), ...(mine.length ? [] : [{ from: accKey, to: null, out: '' }])])}
              styles={{ body: { justifyContent: 'center' } }} />
          </td>);
      }
      if (x.key === 'settle') return <td key={`t:${x.key}`} style={st}><Text span size="xs" c="dimmed">{p ? labelOf(p) : ''}</Text></td>;
      if (x.key === 'new_value') {
        const v = newValue(row);
        const neg = v != null && v < -0.005 && (move ?? 0) < 0;
        return (
          <td key={`t:${x.key}`} style={neg ? { ...st, background: 'var(--mantine-color-red-1)', color: 'var(--mantine-color-red-8)', fontWeight: 600 } : st}
            title={neg ? `The trades settled in ${row.account_code} would leave it negative.` : undefined}>{amount(v)}</td>);
      }
      if (x.key === 'new_pct') {
        const v = newValue(row), g = p ? newGrossOf[p] : null;
        return <td key={`t:${x.key}`} style={st}>{pctFmt(v != null && g ? (v / g) * 100 : null)}</td>;
      }
      if (x.key === 'new_pct_cons') { const v = newValue(row); return <td key={`t:${x.key}`} style={st}>{pctFmt(v != null && newGross ? (v / newGross) * 100 : null)}</td>; }
      if (x.key === 'traded_value') return <td key={`t:${x.key}`} style={st}>{move ? amount(move) : ''}</td>;
      if (x.key === 'traded' && move) {
        const v = target || unit === 'pct' ? (gross ? (move / gross) * 100 : null)
          : unit === 'value' ? move : (p ? convert(move, refCcy, row.currency, ratesFor(p, ctx)) : null);
        return <td key={`t:${x.key}`} style={st}>{target || unit === 'pct' ? pctFmt(v) : amount(v)}</td>;
      }
      return blank;
    }
    if (!k) return blank;
    const traded = !!k.side && k.q > 0;
    const isNew = row.key.startsWith(NEW_PREFIX);
    const a = isNew ? added.find((y) => `${NEW_PREFIX}${y.sec.id}` === row.key) : undefined;
    const open = isOpen(row);
    switch (x.key) {
      case 'split': {
        const count = refs.filter((p) => (k.shares[p]?.q ?? 0) > 0).length;
        return (
          <td key={`t:${x.key}`} style={{ ...st, textAlign: 'center' }}>
            <Tooltip withArrow label={`${open ? 'Close' : 'Open'} the split by portfolio${k.manual ? ' (split by hand)' : ''}`}>
              <ActionIcon size="xs" variant={k.manual ? 'light' : 'subtle'} color={k.manual ? 'orange' : 'gray'}
                aria-label={`Split of ${row.name} by portfolio`} onClick={() => toggleOpen(row)}>
                {open ? <IconChevronDown size={13} /> : <IconChevronRight size={13} />}
              </ActionIcon>
            </Tooltip>
            {traded && <Text span size="xs" c="dimmed"> {count}</Text>}
          </td>);
      }
      case 'buy': {
        if (isNew) {
          return (
            <td key={`t:${x.key}`} style={{ ...st, textAlign: 'center' }} title="New security, bought. Untick to remove it from the run.">
              <Checkbox size="xs" checked aria-label={`Remove ${row.name}`} onChange={() => removeAdded(row.key)} styles={{ body: { justifyContent: 'center' } }} />
            </td>);
        }
        const why = target && !k.manual ? 'Target %: buy or sell is worked out from the target.'
          : !row.authorized ? 'Not approved in Securities: it can only be sold.' : !row.security_id ? 'Not in Securities.' : null;
        return (
          <td key={`t:${x.key}`} style={{ ...st, textAlign: 'center' }} title={why ?? undefined}>
            <Checkbox size="xs" checked={k.side === 'buy'} disabled={!!why} aria-label={`Buy ${row.name}`}
              onChange={() => setSide(row, 'buy')} styles={{ body: { justifyContent: 'center' } }} />
          </td>);
      }
      case 'sell': {
        const why = target && !k.manual ? 'Target %: buy or sell is worked out from the target.' : !row.security_id ? 'Not in Securities: not tradable here yet.'
          : k.held <= 0 ? 'No portfolio of the consolidation holds it: nothing to sell.' : null;
        return (
          <td key={`t:${x.key}`} style={{ ...st, textAlign: 'center' }} title={why ?? undefined}>
            <Checkbox size="xs" checked={k.side === 'sell'} disabled={!!why} aria-label={`Sell ${row.name}`}
              onChange={() => setSide(row, 'sell')} styles={{ body: { justifyContent: 'center' } }} />
          </td>);
      }
      case 'amount': {
        if (k.manual) {
          return (
            <td key={`t:${x.key}`} style={{ ...st, background: 'var(--mantine-color-orange-1)' }}
              title="Split by hand on the portfolios' rows: this is their total. Reset to split the amount again by holdings.">
              <Group gap={4} wrap="nowrap" justify="flex-end">
                <Tooltip withArrow label="Reset the split: back to the amount, split by holdings">
                  <ActionIcon size="xs" variant="subtle" color="orange" aria-label={`Reset the split of ${row.name}`} onClick={() => resetSplit(row.key)}>
                    <IconRestore size={13} />
                  </ActionIcon>
                </Tooltip>
                <span>{traded ? unitFmt(inUnit(k.q, k.unitValue)) : '-'}</span>
              </Group>
            </td>);
        }
        const note = k.notes.join('; ');
        return (
          <td key={`t:${x.key}`} style={{ ...st, background: note ? 'var(--mantine-color-orange-1)' : st.background }}
            title={note ? `Adjusted: ${note}. Quantity traded: ${qtyFmt(k.q)}.` : undefined}>
            <Group gap={4} wrap="nowrap" justify="flex-end">
              {note && <IconInfoCircle size={13} color="var(--mantine-color-orange-7)" />}
              <input type="number" min={0} step="any" value={e.amount} disabled={!target && !e.side} aria-label={`Amount for ${row.name}`}
                onChange={(ev) => setEntry(row.key, { amount: ev.currentTarget.value })}
                onBlur={() => {
                  if (!target) {
                    // As for one portfolio (Pedro 2026-10-23): an amount the app adjusted is rewritten to
                    // the adjusted figure on leaving the field — and (smoke 2026-10-30, Pedro: "I am
                    // struggling to understand how…") the change is said, naming why.
                    if (!e.side || !k.notes.length) return;
                    const adjusted = fromQuantity(k.q, unit, k.unitValue, gross);
                    if (adjusted == null || Math.abs(adjusted - Number(e.amount)) < 1e-9) return;
                    setEntry(row.key, { amount: clean(adjusted) });
                    notifications.show({ color: k.capped ? 'red' : 'orange', autoClose: 10000, title: 'Amount adjusted',
                      message: `${row.name}: ${unitFmt(Number(e.amount))} became ${unitFmt(adjusted)} — ${k.notes.join('; ')}.` });
                    return;
                  }
                  const want = n(e.amount);
                  if (want != null && want > 100) {
                    setEntry(row.key, { amount: '100' });
                    notifications.show({ color: 'red', autoClose: 10000, title: 'Target capped',
                      message: `${row.name}: a target can be at most 100% of the consolidation. ${pctFmt(want)} was cut to 100.00%.` });
                    return;
                  }
                  // A not-approved security's target may only be equal to or below its weight now.
                  if (row.authorized || !row.security_id || isNew || want == null) return;
                  const cur = weightNow(row.security_id);
                  if (want <= cur + 0.005) return;
                  setEntry(row.key, { amount: clean(cur) });
                  notifications.show({ color: 'red', autoClose: 10000, title: 'Target rejected',
                    message: `${row.name} is not Approved in Securities: its target can only be equal to or smaller than its weight now (${pctFmt(cur)}). ${pctFmt(want)} was not accepted.` });
                }}
                style={{ ...inputStyle, minWidth: 40 }} />
            </Group>
          </td>);
      }
      case 'price': {
        if (!a) return <td key={`t:${x.key}`} style={st}>{fmt(measure(row, TOTAL, 'price', { ...ctx, inView: refs }), 2, 6)}</td>;
        const why = a.source == null ? 'No price found in the app: type one.'
          : `Price from ${SOURCE_TEXT[a.source]}${a.priceDate ? ` (${a.priceDate})` : ''}.${a.factorGuessed && a.factor < 1 ? ' Taken as per 100 nominal (a bond).' : ''}`;
        return (
          <td key={`t:${x.key}`} title={why} style={{ ...st, background: n(a.price) ? 'var(--mantine-color-yellow-2)' : 'var(--mantine-color-red-1)' }}>
            <input type="number" min={0} step="any" value={a.price} aria-label={`Price for ${row.name}`}
              onChange={(ev) => editAdded(row.key, { price: ev.currentTarget.value, source: 'typed', priceDate: c.as_of })} style={inputStyle} />
          </td>);
      }
      case 'accrued_unit': {
        if (!a) return blank;
        const missing = a.factor < 1 && a.accrued.trim() === '';
        return (
          <td key={`t:${x.key}`} title={missing ? 'A bond is traded with its accrued interest: type it per unit (0 if none).' : `In ${a.accruedCcy}.`}
            style={{ ...st, background: missing ? 'var(--mantine-color-red-1)' : a.accrued.trim() ? 'var(--mantine-color-yellow-2)' : st.background }}>
            <Group gap={4} wrap="nowrap">
              <input type="number" step="any" value={a.accrued} aria-label={`Accrued interest per unit for ${row.name}`}
                onChange={(ev) => editAdded(row.key, { accrued: ev.currentTarget.value })} style={inputStyle} />
              <Text size="xs" c="dimmed">{a.accruedCcy}</Text>
            </Group>
          </td>);
      }
      case 'settle': {
        if (isNew) {
          return (
            <td key={`t:${x.key}`} style={{ ...st, overflow: 'visible', background: pick[row.key] ? st.background : 'var(--mantine-color-red-1)' }}
              title="No portfolio of the consolidation holds it: choose the portfolio to buy it in (S4-Q3). To split it over several, type each portfolio's part on its row (orange column).">
              <Select size="xs" placeholder="Buy in…" value={pick[row.key] ?? null}
                data={[...members.map((m) => ({ value: m.ref, label: m.label })), { value: CASH_PICK, label: 'Split by available cash' },
                  { value: OPT_PICK, label: 'Let the optimizer choose (step 2)' }]}
                onChange={(v) => void choosePick(row.key, v)} comboboxProps={{ withinPortal: true }} aria-label={`Portfolio to buy ${row.name} in`} />
            </td>);
        }
        const used = [...new Set(refs.filter((p) => (k.shares[p]?.q ?? 0) > 0).map((p) => k.shares[p].settle ?? '?'))];
        return <td key={`t:${x.key}`} style={st} title={used.length ? 'Open the row to change a portfolio\'s account.' : undefined}>{used.join(', ')}</td>;
      }
      case 'keep': {
        const need = refs.filter((p) => k.shares[p]?.notApprovedKept);
        if (!need.length) return blank;
        const missing = need.filter((p) => !(notes[pk(row.key, p)] ?? '').trim()).length;
        return (
          <td key={`t:${x.key}`} style={{ ...st, background: missing ? 'var(--mantine-color-yellow-3)' : 'var(--mantine-color-yellow-1)' }}
            title="Not approved: kept (in full or in part). Open the row and give each portfolio's reason.">
            {missing ? `${missing} reason${missing === 1 ? '' : 's'} to give` : 'Reasons given'}
          </td>);
      }
      case 'traded': return <td key={`t:${x.key}`} style={st}>{traded ? unitFmt(signed(k.side, inUnit(k.q, k.unitValue) ?? 0)) : ''}</td>;
      case 'traded_qty': return <td key={`t:${x.key}`} style={st}>{traded ? qtyFmt(signed(k.side, k.q)) : ''}</td>;
      case 'traded_value': return <td key={`t:${x.key}`} style={st}>{traded ? amount(signed(k.side, k.value)) : ''}</td>;
      case 'new_qty': return <td key={`t:${x.key}`} style={st}>{qtyFmt(k.held + (traded ? signed(k.side, k.q) : 0))}</td>;
      case 'new_value': return <td key={`t:${x.key}`} style={st}>{amount(newValue(row))}</td>;
      case 'new_pct': return <td key={`t:${x.key}`} style={st} title="A security's row spans several portfolios: see each portfolio's row, or New % of consolidation." />;
      case 'new_pct_cons': { const v = newValue(row); return <td key={`t:${x.key}`} style={st}>{pctFmt(v != null && newGross ? (v / newGross) * 100 : null)}</td>; }
      default: return blank;
    }
  });

  /** S4-Q2: a security row's per-portfolio rows (every portfolio of the consolidation). */
  const subRows = (row: ConsolidatedRow) => {
    if (!active || row.is_cash || row.is_loan || !isOpen(row)) return [];
    const k = calc[row.key];
    if (!k) return [];
    return members.map((m) => {
      const p = m.ref;
      const s = k.shares[p];
      const key = pk(row.key, p);
      const shade = blockedKeys.has(key) ? 'var(--mantine-color-red-1)' : (s?.q ?? 0) > 0 ? 'var(--mantine-color-orange-0)' : null;
      const label = (
        <Text span size="xs" c="dimmed">↳ {m.label}{s && s.held > 0 ? ` · holds ${qtyFmt(s.held)}` : ' · does not hold it'}</Text>
      );
      const cells = (bg: string) => tcols.map((x) => {
        const st = cellStyle(x, bg);
        const blank = <td key={`t:${x.key}`} style={st} />;
        if (!s) return blank;
        const traded = !!k.side && s.q > 0;
        // B (Pedro 2026-10-30, "yes to all"): a portfolio's row reads in % of THAT portfolio; the
        // consolidation's % stays on the security's row.
        const pctMode = target || unit === 'pct';
        const gp = grossIn(p, ctx);
        const shareIn = (q: number) => (pctMode ? (s.unitValue != null && gp ? ((q * s.unitValue) / gp) * 100 : null) : inUnit(q, s.unitValue));
        const pctTip = pctMode ? `% of ${m.label} (not of the consolidation)` : undefined;
        switch (x.key) {
          // Pedro 2026-10-30 ("Yes, build (1) and (2)"): the entry column is in its heading's unit on
          // every row — % of the consolidation — so a security's portfolio rows add up to its amount.
          // Step-7 smoke (Pedro: "open all 3 trade lines (in orange columns) with Quantity boxes and
          // Settle in boxes … place the cursor in the Q box of 0002"): each portfolio's part is TYPED
          // here, in the unit; typing splits the row by hand (Traded Quantity is the result).
          case 'amount': {
            const side = (entries[row.key]?.side ?? null) || k.side;   // C-417
            const why = !side ? 'Tick Buy or Sell on the security\'s row first (or type its Target %).'
              : side === 'sell' && s.held <= 0 ? 'This portfolio does not hold it: nothing to sell.'
                : side === 'buy' && !row.authorized ? 'Not approved: it cannot be bought.' : null;
            const subUnit: Unit = target ? 'pct' : unit;
            const typedQ = k.manual ? n(manual[row.key]?.[p]) : null;
            // A portfolio's part is shown from its quantity (display only: typing replaces it), so it can
            // be rounded for reading — 2 decimals in % and value (smoke 2026-10-30: "4.30848659").
            const shown = (q: number | null) => {
              if (q == null || q <= 0) return '';
              const v = fromQuantity(q, subUnit, s.unitValue, gross);
              if (v == null) return '';
              return subUnit === 'quantity' ? clean(v) : String(Math.round(v * 100) / 100);
            };
            const val = shareText[key] ?? (k.manual ? (subUnit === 'quantity' ? (manual[row.key]?.[p] ?? '') : shown(typedQ)) : shown(s.q));
            return (
              <td key={`t:${x.key}`} style={{ ...st, background: s.note ? 'var(--mantine-color-orange-1)' : st.background }}
                title={why ?? (s.note ? `Adjusted: ${s.note}. Quantity traded: ${qtyFmt(s.q)}.`
                  : `Type ${m.label}'s part, in ${subUnit === 'pct' ? '% of the consolidation' : subUnit === 'value' ? refCcy : 'quantity'}: the row is then split by hand.`)}>
                <input type="number" min={0} step="any" value={val} disabled={!!why} aria-label={`Amount of ${row.name} in ${m.label}`}
                  ref={(el) => { shareInputs.current[key] = el; }}
                  onChange={(ev) => {
                    const t = ev.currentTarget.value;
                    setShareText((x2) => ({ ...x2, [key]: t }));
                    const q = t.trim() === '' ? null : toQuantity(Number(t), subUnit, s.unitValue, gross);
                    typeShare(row, p, q == null ? '' : clean(q));
                  }}
                  // A quantity the app adjusted (e.g. 150 typed where 100 is held: no short sales; below
                  // the minimum lot) is rewritten on leaving the field, and the change is said.
                  onBlur={() => {
                    setShareText((x2) => { const y = { ...x2 }; delete y[key]; return y; });
                    const typed = n(manual[row.key]?.[p]);
                    if (!k.manual || typed == null || Math.abs(typed - s.q) < 1e-9) return;
                    setManual((mm) => ({ ...mm, [row.key]: { ...mm[row.key], [p]: s.q > 0 ? clean(s.q) : '' } }));
                    notifications.show({ color: 'orange', autoClose: 10000, title: 'Quantity adjusted',
                      message: `${row.name} · ${m.label}: ${qtyFmt(typed)} became ${qtyFmt(s.q)}${s.note ? ` — ${s.note}` : ''}.` });
                  }}
                  style={inputStyle} />
              </td>);
          }
          case 'price': {
            const tip = s.priceSource && s.priceSource !== 'own' ? `Price from ${SOURCE_TEXT[s.priceSource] ?? s.priceSource}.` : undefined;
            return <td key={`t:${x.key}`} style={tip ? { ...st, background: 'var(--mantine-color-yellow-1)' } : st} title={tip}>{fmt(s.price, 2, 6)}</td>;
          }
          case 'accrued_unit': return <td key={`t:${x.key}`} style={st}>{s.accruedPerUnit ? `${fmt(s.accruedPerUnit, 2, 6)} ${m.ccy}` : ''}</td>;
          case 'settle': {
            // Step-7 smoke: a Settle in box on every portfolio row, filled only where that portfolio
            // trades, is the one chosen to buy, or was set by hand.
            const options = cashOf(p).map((a) => ({ value: a.code, label: `${a.code} (${a.ccy})` }));
            const filled = traded || pick[row.key] === p || !!settle[key];
            return (
              <td key={`t:${x.key}`} style={{ ...st, overflow: 'visible', background: traded && !s.settle ? 'var(--mantine-color-red-1)' : st.background }}>
                <Select size="xs" data={options} value={filled ? s.settle : null} allowDeselect={false}
                  placeholder={options.length ? 'Cash account…' : 'No cash account'}
                  aria-label={`Settle ${row.name} in ${m.label}`} comboboxProps={{ withinPortal: true }}
                  onChange={(v) => { if (v) { setSettle((x2) => ({ ...x2, [key]: v })); touch(); } }} />
              </td>);
          }
          case 'keep': {
            if (!s.notApprovedKept) return blank;
            const v = notes[key] ?? '';
            return (
              <td key={`t:${x.key}`} style={{ ...st, background: v.trim() ? 'var(--mantine-color-yellow-1)' : 'var(--mantine-color-yellow-3)' }}
                title="Not approved: kept (in full or in part) in this portfolio. Give the reason, e.g. tax, lock-up, client instruction.">
                <input type="text" value={v} placeholder="Reason (required)" aria-label={`Reason to keep ${row.name} in ${m.label}`}
                  onChange={(ev) => { const val = ev.currentTarget.value; setNotes((x2) => ({ ...x2, [key]: val })); touch(); }}
                  style={{ width: '100%', font: 'inherit', border: '1px solid var(--mantine-color-yellow-7)', borderRadius: 4, padding: '1px 4px' }} />
              </td>);
          }
          case 'traded': return <td key={`t:${x.key}`} style={st} title={pctTip}>{traded ? unitFmt(signed(k.side, shareIn(s.q) ?? 0)) : ''}</td>;
          case 'traded_qty': return <td key={`t:${x.key}`} style={st}>{traded ? qtyFmt(signed(k.side, s.q)) : ''}</td>;
          case 'traded_value': return <td key={`t:${x.key}`} style={st}>{traded ? amount(signed(k.side, s.value)) : ''}</td>;
          case 'new_qty': return <td key={`t:${x.key}`} style={st}>{qtyFmt(s.held + (traded ? signed(k.side, s.q) : 0))}</td>;
          case 'new_value': return <td key={`t:${x.key}`} style={st}>{amount(newValueIn(row, p))}</td>;
          case 'new_pct_cons': {
            const v = newValueIn(row, p);
            return <td key={`t:${x.key}`} style={st}>{pctFmt(v != null && newGross ? (v / newGross) * 100 : null)}</td>;
          }
          case 'new_pct': {
            const v = newValueIn(row, p), g = newGrossOf[p];
            return <td key={`t:${x.key}`} style={st} title={`% of ${m.label}`}>{pctFmt(v != null && g ? (v / g) * 100 : null)}</td>;
          }
          default: return blank;
        }
      });
      return { key, label, shade, cells };
    });
  };

  // ── total rows ───────────────────────────────────────────────────────────────────────────
  const sticky = (bottom?: number): React.CSSProperties => (bottom == null ? {} : { position: 'sticky', bottom, zIndex: 1 });
  const blankCells = (bg: string, bold = false, bottom?: number) =>
    tcols.map((x) => <td key={`t:${x.key}`} style={{ ...cellStyle(x, bg, bold), ...sticky(bottom) }} />);
  const footSums = (which: Side | 'all') => {
    let traded = 0, value = 0, newV = 0, any = false;
    for (const r of rows) {
      const k = calc[r.key];
      if (!k || !k.side || k.q <= 0 || (which !== 'all' && k.side !== which)) continue;
      any = true;
      traded += signed(k.side, inUnit(k.q, k.unitValue) ?? 0);
      value += signed(k.side, k.value);
      newV += newValue(r) ?? 0;
    }
    if (which === 'all') return { any, traded, value, newV: newGross, newPct: newGross ? 100 : null };
    return { any, traded, value, newV: any ? newV : null, newPct: null };
  };
  const pctUnit = unit === 'pct' || target;
  const foot = (label: string, tip: string, which: Side | 'all') => {
    const s = footSums(which);
    return {
      label, tip,
      cells: (bottom: number) => tcols.map((x) => {
        let v: React.ReactNode = '';
        if (x.key === 'traded_value') v = amount(s.any ? s.value : 0);
        else if (s.any && x.key === 'traded' && (pctUnit || unit === 'value')) v = pctUnit ? pctFmt(s.traded) : amount(s.traded);
        else if (x.key === 'new_value' && s.newV != null) v = amount(s.newV);
        else if (x.key === 'new_pct_cons' && which === 'all') v = pctFmt(s.newPct);
        return <td key={`t:${x.key}`} style={{ ...cellStyle(x, 'var(--mantine-color-gray-2)', true), ...sticky(bottom) }}>{v}</td>;
      }),
    };
  };
  const footRows = active ? [
    foot('Buy total', `The securities bought in this run, every portfolio, in ${refCcy}.`, 'buy'),
    foot('Sell total', `The securities sold in this run, every portfolio, in ${refCcy} (negative: cash coming in).`, 'sell'),
    foot('Net total', `Purchases less sales, in ${refCcy}; New Value and New % are the whole consolidation after the trades (loans left out).`, 'all'),
  ] : [];

  // ── toolbar ──────────────────────────────────────────────────────────────────────────────
  const isTraded = (r: ConsolidatedRow) => {
    if (r.is_cash) { const p = refs.find((x) => r.by_portfolio[x]); return !!p && !!cashMove[pk(p, r.account_code ?? '')]; }
    return r.key.startsWith(NEW_PREFIX) || (!!calc[r.key]?.side && (calc[r.key]?.q ?? 0) > 0);
  };
  /** C (Pedro 2026-10-30): a traded row is open unless closed by hand; any other row when opened by hand. */
  function isOpen(r: ConsolidatedRow) { return !closed.has(r.key) && (expanded.has(r.key) || isTraded(r)); }
  function toggleOpen(r: ConsolidatedRow) {
    const open = isOpen(r);
    setExpanded((s) => { const y = new Set(s); if (open) y.delete(r.key); else y.add(r.key); return y; });
    setClosed((s) => { const y = new Set(s); if (open) y.add(r.key); else y.delete(r.key); return y; });
  }
  const name = consolidation?.name ?? '';
  const button = !enabled ? null : active ? (
    <Button size="xs" variant="default" onClick={() => void leave()}>Leave trade mode</Button>
  ) : (
    <Tooltip withArrow multiline w={340} label={`Trade consolidation ${name} as one: one row per security, split over its portfolios; saved as a run (one draft per portfolio).`}>
      <span><Button size="xs" leftSection={<IconArrowsExchange size={14} />} onClick={enter}>Trade</Button></span>
    </Tooltip>
  );
  const runOptions = [{ value: '__new__', label: 'New run' }, ...runs.map((r) => ({ value: r.id,
    label: `${r.serial} · ${r.title ?? 'Untitled'} · ${r.drafts.reduce((s, d) => s + d.line_count, 0)} orders${r.updated_by_name ? ` · ${r.updated_by_name}` : ''} · ${new Date(r.updated_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}${r.locked ? ` · ${r.status_label}` : ''}` }))];
  const locked = !!runId && !!lastSaved?.locked && !override;
  const noTrades = !Object.values(calc).some((k) => k.side && k.q > 0) && added.length === 0 && transfers.length === 0;
  const defaultModelNote = consolidation?.model_id
    ? `${consolidation.model_name} is consolidation ${name}'s model: chosen for you; you can choose another.` : null;
  /** TC-S5-GUIDE: the first step not done (its detail opens unless another was clicked). */
  const shownStep = blockers.length ? 1 : splitState === 'none' ? 2 : !cashOk ? 3 : 4;
  /** Step 1 "Show": open the row and bring it into view. */
  const showRow = (key: string) => {
    setClosed((s) => { const x = new Set(s); x.delete(key); return x; });
    setExpanded((s) => new Set(s).add(key));
    if (onlyTraded) setOnlyTraded(false);
    window.setTimeout(() => document.getElementById(`trade-row-${key}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 80);
  };
  const panel = !active ? null : (
    <Stack gap={4}>
      <Group gap="xs" align="flex-end" wrap="wrap"
        style={{ background: 'var(--mantine-color-orange-0)', border: '1px solid var(--mantine-color-orange-3)', borderRadius: 6, padding: '6px 8px' }}>
        {/* 5c smoke (Pedro 2026-11-01): the title on a row of its own, so the controls fit one row. */}
        <Group gap={6} w="100%" wrap="nowrap">
          <Text size="sm" fw={700} c="orange.8">Trade mode · consolidation {name}</Text>
          {/* TC-S5-CLUTTER: the always-open explanation, now on demand. */}
          <Popover width={520} position="bottom-start" withArrow shadow="md">
            <Popover.Target>
              <Anchor size="xs" component="button" type="button">(?) How trade mode works</Anchor>
            </Popover.Target>
            <Popover.Dropdown>
              <Text size="xs">
                One row per security for the whole consolidation ({members.map((m) => m.label).join(', ')}). An amount is split over the
                portfolios where the security is held, pro rata; open a row (By portfolio) to see each portfolio's part, change its
                settlement account, or type its part (orange column) to split by hand. Saved as a run: one draft per portfolio, together.
                Cash moves between accounts and between the portfolios with Cash transfers… (or a cash row's Sell tick).
                Once there are trades, the run's steps (1 Securities · 2 Split · 3 Cash · 4 Save) show the next action and guide you to
                saving. Every save also makes the run's documents; its emails and steps are on its panel in Trades.
              </Text>
            </Popover.Dropdown>
          </Popover>
        </Group>
        <Select size="xs" label="Run" w={280} allowDeselect={false} data={runOptions}
          value={runId ?? '__new__'} onChange={(v) => void loadRun(v === '__new__' ? null : v)} />
        <TextInput size="xs" label="Title" w={200} placeholder="e.g. Rebalance November" value={title}
          onChange={(e) => { setTitle(e.currentTarget.value); touch(); }} />
        <Tooltip withArrow disabled={!target} label="Target % is on: turn it off to type in a unit again.">
          <Stack gap={2}>
            <Text size="xs" fw={500}>Unit</Text>
            <SegmentedControl size="xs" value={unit} onChange={(v) => changeUnit(v as Unit)} disabled={target}
              data={[{ value: 'quantity', label: 'Quantity' }, { value: 'value', label: `Value (${R})` }, { value: 'pct', label: '% of consolidation' }]} />
          </Stack>
        </Tooltip>
        <Stack gap={2}>
          <Text size="xs" fw={500}>Work out the trades from</Text>
          <Group gap={4}>
            <Tooltip withArrow multiline w={300} label="Type, for each security, the % of the consolidation you want it to be after the trade: the app buys or sells the difference, split over the portfolios where it is held.">
              <Button size="xs" variant={target ? 'filled' : 'default'} onClick={() => toggleTarget(!target)}>Target %</Button>
            </Tooltip>
            <Menu shadow="md" position="bottom-start" withArrow>
              <Menu.Target>
                <Tooltip withArrow multiline w={320} label="Work out the trades that bring the consolidation to a model, a portfolio or another consolidation (its weights as % of the consolidation). Refused as a whole if the target holds more of a not-approved security than the consolidation.">
                  <Button size="xs" variant="default" loading={rebBusy}>Rebalance to…</Button>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Rebalance to</Menu.Label>
                <Menu.Item onClick={() => setRebPick('model')}>Model…</Menu.Item>
                <Menu.Item onClick={() => setRebPick('portfolio')}>Portfolio…</Menu.Item>
                <Menu.Item onClick={() => setRebPick('consolidation')}>Consolidation…</Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <ConsolidationPicker opened={rebPick === 'consolidation'} onClose={() => setRebPick(null)} busy={rebBusy} action="Rebalance"
              onChoose={(cons) => void rebalanceToConsolidation(cons)} target={<span />}
              intro="Choose the consolidation to copy: each security's Target % becomes its weight there; 0 for the ones it does not hold, which are sold. The trades on screen are replaced." />
            <ModelPicker opened={rebPick === 'model'} onClose={() => setRebPick(null)} busy={rebBusy} action="Rebalance"
              defaultId={consolidation?.model_id ?? null} defaultNote={defaultModelNote}
              onChoose={(id, nm) => void rebalanceToModel(id, nm)} target={<span />}
              intro="Choose the model: each security's Target % becomes its weight in the model (0 for the ones it does not hold, which are sold). The trades on screen are replaced." />
            <PortfolioPicker value={[]} onChange={(v) => void rebalanceToPortfolio(v[0] ?? null)}
              single={{ button: 'Rebalance to…', action: 'Rebalance', exclude: null, busy: rebBusy,
                opened: rebPick === 'portfolio', onClose: () => setRebPick(null), target: <span />,
                intro: "Choose the portfolio to copy: the consolidation's trades are worked out to reach its positions (as % of the consolidation). The trades on screen are replaced." }} />
          </Group>
        </Stack>
        <Tooltip withArrow multiline w={300} label="Pre-fills each portfolio's settlement account (one of that portfolio's cash accounts); open a row to change one. Changing it here re-fills every trade.">
          <Select size="xs" label="Settle in" w={210} allowDeselect={false}
            data={[{ value: 'ref', label: "Each portfolio's reference currency" }, { value: 'sec', label: 'Security currency' }]}
            value={std} onChange={(v) => v && applyStd(v as Std)} />
        </Tooltip>
        <Checkbox size="xs" label="Traded only" checked={onlyTraded} onChange={(ev) => setOnlyTraded(ev.currentTarget.checked)} mb={6} />
        {/* 4c: every transfer of the run, within a portfolio or between its portfolios. */}
        <Tooltip withArrow multiline w={300} label="Move cash between accounts: within a portfolio, or between the consolidation's portfolios (each such transfer is confirmed loudly). Also from a cash row's Sell tick.">
          {/* Smoke 2026-10-30 ("Not very evident"): coloured, with an icon; orange while an account is overdrawn. */}
          <Button size="xs" variant="light" color={overdrawn.length ? 'orange' : 'blue'} leftSection={<IconArrowsExchange size={14} />}
            onClick={() => openTransfers(transfers.map(toRow))}>
            Cash transfers…{transfers.length ? ` (${transfers.length})` : ''}
          </Button>
        </Tooltip>
        {/* TC-S5-GUIDE (G1–G4): the optimizer is step 2 of the run's steps, under the bar — no button here. */}
        {(() => {
          const secRows = rows.filter((r) => !r.is_cash && !r.is_loan);
          const anyOpen = secRows.some(isOpen);
          return (
            <Tooltip withArrow multiline w={280} label="A traded row opens to its portfolios by itself; close or open any row with its arrow (By portfolio).">
              <Button size="xs" variant="subtle" mb={2} onClick={() => {
                if (anyOpen) { setExpanded(new Set()); setClosed(new Set(secRows.map((r) => r.key))); }
                else { setClosed(new Set()); setExpanded(new Set(secRows.filter(isTraded).map((r) => r.key))); }
              }}>
                {anyOpen ? 'Close every split' : 'Open the traded rows\' splits'}
              </Button>
            </Tooltip>
          );
        })()}
        <Tooltip withArrow multiline w={300} disabled={!locked && !saveWhy}
          label={locked ? 'No longer a draft: it cannot be changed. Save as a new run to start a new trade from it.' : saveWhy}>
          <span>
            <Button size="xs" onClick={() => askSave(false)} loading={saving} disabled={locked || !!saveWhy}
              title={!locked && !saveWhy ? 'Saves the run: one draft per portfolio of the consolidation, together' : undefined}>
              {runId ? 'Save run' : 'Save as a run'}
            </Button>
          </span>
        </Tooltip>
        {runId && (
          <Tooltip withArrow multiline w={300} disabled={!saveWhy} label={saveWhy}>
            <span><Button size="xs" variant="light" onClick={() => askSave(true)} loading={saving} disabled={!!saveWhy}>Save as a new run</Button></span>
          </Tooltip>
        )}
        <Tooltip withArrow disabled={!noTrades} label="No trades on screen to clear.">
          <span><Button size="xs" variant="default" onClick={() => setConfirmClear(true)} disabled={noTrades}>Clear all trades</Button></span>
        </Tooltip>
        {runId && (
          <Tooltip withArrow disabled={!locked} label="No longer a draft: it cannot be deleted.">
            <span><Button size="xs" variant="light" color="red" disabled={locked} onClick={() => setConfirmDelete(true)}>Delete run</Button></span>
          </Tooltip>
        )}
        <AdminOverrideSwitch />
        <Text size="xs" c="dimmed" ml="auto">A run changes nothing in the portfolios; only Upload positions does.</Text>
      </Group>
      {/* TC-S5-GUIDE (G1, G2): the run's steps, for every run with trades (not for a locked one). */}
      {!noTrades && !locked && (
        <RunGuide step={guideStep ?? shownStep} onStep={setGuideStep}
          sec={{ items: guideItems, onShow: showRow }}
          split={{ state: splitState, why: blockers.length ? 'Finish step 1 first: every security must be settled before the split.' : null,
            keepWhy: leftToOpt.length ? `Left to the optimizer at step 1 (Buy in): ${leftToOpt.join(', ')}. Optimize, or choose their Buy in at step 1.` : null,
            onKeep: () => { setKeptSplit(true); setOptUsed(null); setGuideStep(null); },
            onOptimize: () => setOptOpen(true),
            optimizedNote: optUsed ? `Optimized with ${optUsed.preset_label}, tolerance ${optUsed.tolerance_bps} bps. Optimize again to re-split; a change by hand makes the split yours.` : null }}
          cash={{ ready: splitState !== 'none', hard: cashHard, soft: cashSoft, accepted: cashAccepted === cashSig && cashHard.length + cashSoft.length > 0,
            onAccept: (on) => setCashAccepted(on ? cashSig : null), onSuggest: suggestCash,
            suggestLabel: usePlanX ? "Use the model plan's cash transfers…" : 'Suggest cash transfers…',
            // TC-S5-ONESTEP (Pedro 2026-11-02: "it gets stuck in a circle"): greyed, with the reason, when no
            // transfer can fix what is left — never a button that opens an empty window.
            suggestWhy: usePlanX || splitState === 'none' || cashHard.length + cashSoft.length === 0 || cashPlanRows(transfers).length ? null
              : 'No cash transfer can fix what is left (within each portfolio, then from another portfolio of the same wrapper above its minimum cash): change the trades, or accept the cash as it is.',
            // TC-S5-PLANX (Pedro 2026-11-01): the plan's own transfers stay visible at step 3 until used.
            plan: keptSplit && !optUsed && !planXfersUsed ? planXfers.map((x) =>
              `${x.from ? accLabel(x.from) : '?'} → ${x.to ? accLabel(x.to) : '?'}: ${amount(Number(x.out))}${x.from ? ` ${accCcy(splitAcc(x.from).p, splitAcc(x.from).code)}` : ''}`) : [] }}
          save={{ why: saveWhy }} />
      )}
      {!isLatest && (
        <Alert color="yellow" py={4}>Trades are always set on the latest positions ({latest}); this view shows {c.as_of}.{' '}
          <Button size="compact-xs" variant="light" onClick={onLatest}>Show {latest}</Button>
        </Alert>
      )}
      {/* TC-S5-CLUTTER: the explanation moved behind the (?) beside the trade-mode title. */}
      {runId && lastSaved?.locked && (
        <Alert color="gray" py={4}>
          {lastSaved.serial} is {lastSaved.status_label}: only a Draft run can be changed{override ? ' — your Admin override (Beta) is on (each change is logged)' : '. Save as a new run to start a new trade from it'}.
        </Alert>
      )}
      {/* TC-S5-CLUTTER: the same list is step 1 of the run's steps — shown here only when the steps are not. */}
      {blockers.length > 0 && (noTrades || locked) && (
        <Alert color="red" variant="filled" py={4} icon={<IconAlertTriangle size={18} />}
          title={`To fix before saving: ${blockers.length} problem${blockers.length === 1 ? '' : 's'} (the rows concerned are red)`}>
          <Stack gap={0}>{blockers.map((b) => <Text key={b} size="xs">{b}</Text>)}</Stack>
        </Alert>
      )}
      {rebNote && (() => {
        // As for one portfolio (M-REB-LOUD): red while something is still to do — a security with no
        // portfolio to buy it in (counted live) or what the rebalance could not do; grey once it is
        // only information.
        const homeless = rows.filter((r) => calc[r.key]?.noHome && pick[r.key] !== OPT_PICK).length;
        const color = homeless || rebNote.problems.length ? 'red' : 'gray';
        // TC-S5-CLUTTER (Pedro 2026-11-01): one line; the detail on demand.
        const summary = [
          rebNote.info ? rebNote.info.replace(/\.$/, '') : null,
          rebNote.problems.length ? `${rebNote.problems.length} thing${rebNote.problems.length === 1 ? '' : 's'} it could not do` : null,
          rebNote.noIsin ? `${rebNote.noIsin} line(s) without an ISIN left out` : null,
        ].filter(Boolean).join(' · ');
        return (
          <Alert color={color} py={2} px="xs" withCloseButton onClose={() => setRebNote(null)}>
            <Group gap={6} wrap="nowrap">
              <Text size="xs" fw={700} style={{ flex: 'none' }}>Rebalanced to {rebNote.label}{rebNote.date ? ` (positions of ${rebNote.date})` : ''}</Text>
              <Text size="xs" truncate style={{ flex: 1, minWidth: 0 }} c={rebNote.problems.length ? 'red.8' : undefined}>{summary}</Text>
              <Anchor size="xs" component="button" type="button" style={{ flex: 'none' }} onClick={() => setRebOpen((o) => !o)}>
                {rebOpen ? 'Hide details' : 'Details'}
              </Anchor>
            </Group>
            {rebOpen && (
              <Stack gap={0} mt={4}>
                <Text size="xs">Each security's Target % is its weight there, as % of the consolidation; each trade is split over the portfolios where the security is held. The lot rules apply in each portfolio, so the result is close to the target, not always exact.</Text>
                {rebNote.noIsin > 0 && <Text size="xs" c="yellow.9">{rebNote.noIsin} line(s) without an ISIN were left out.</Text>}
                {rebNote.info && <Text size="xs">{rebNote.info}</Text>}
                {homeless > 0 && <Text size="xs" c="red.8" fw={500}>{homeless} security(ies) no portfolio holds: pick the portfolio to buy each in (its row's Buy in), or leave it to step 2's optimizer.</Text>}
                {rebNote.problems.map((p) => <Text key={p} size="xs" c="red.8" fw={500}>{p}</Text>)}
              </Stack>
            )}
          </Alert>
        );
      })()}
      <Modal opened={rebReject != null} onClose={() => setRebReject(null)} centered size="lg" title={<Text fw={700} c="red.8">Rebalance rejected</Text>}>
        <Stack>
          <Alert color="red" py="xs">
            {rebReject?.label} holds more of {rebReject && rebReject.offenders.length > 1 ? 'securities that are' : 'a security that is'} not
            Approved in Securities than the consolidation does. A not-approved security can only be kept or reduced, never increased,
            so this rebalance cannot be done. Nothing on screen was changed.
          </Alert>
          <Table withTableBorder striped fz="sm">
            <Table.Thead>
              <Table.Tr><Table.Th>Not approved</Table.Th><Table.Th ta="right">Weight here</Table.Th><Table.Th ta="right">Weight in {rebReject?.label}</Table.Th></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rebReject?.offenders.map((o) => (
                <Table.Tr key={o.name}><Table.Td>{o.name}</Table.Td><Table.Td ta="right">{pctFmt(o.now)}</Table.Td><Table.Td ta="right">{pctFmt(o.want)}</Table.Td></Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group justify="flex-end"><Button onClick={() => setRebReject(null)}>OK</Button></Group>
        </Stack>
      </Modal>
      {/* A (Pedro 2026-10-30, "yes to all"): a security no portfolio holds — ask at once where to buy it. */}
      {(() => {
        const a = askBuyIn ? added.find((x) => `${NEW_PREFIX}${x.sec.id}` === askBuyIn) : undefined;
        const ccy = a?.sec.currency ?? '';
        const cashIn = (p: string, which: 'ccy' | 'all') => {
          let s = 0, any = false;
          for (const acc of cashOf(p)) {
            if (which === 'ccy' && acc.ccy !== ccy) continue;
            const r = rows.find((x) => x.key === acc.key);
            const v = r ? measure(r, p, which === 'ccy' ? 'value_pos' : 'value_ref', ctx) : null;
            if (v != null) { s += v; any = true; }
          }
          return any ? s : null;
        };
        return (
          <Modal opened={!!a} onClose={() => setAskBuyIn(null)} returnFocus={false} title={a ? `Buy ${a.sec.description} in which portfolio?` : ''} size="lg">
            <Stack>
              <Text size="sm">No portfolio of consolidation {name} holds it, so the app cannot split it by holdings. Choose the portfolio that buys it; after you choose, the cursor waits in that portfolio's amount box; you can also split it over several by typing each portfolio's part on its row.</Text>
              <Table withTableBorder striped fz="sm">
                <Table.Thead>
                  <Table.Tr><Table.Th>Portfolio</Table.Th><Table.Th ta="right">Cash in {ccy}</Table.Th><Table.Th ta="right">All its cash ({refCcy})</Table.Th><Table.Th /></Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {members.map((m) => {
                    const own = cashIn(m.ref, 'ccy');
                    return (
                      <Table.Tr key={m.ref}>
                        <Table.Td>{m.label}</Table.Td>
                        <Table.Td ta="right">{own == null ? <Text span size="xs" c="dimmed">no {ccy} account</Text> : `${amount(own)} ${ccy}`}</Table.Td>
                        <Table.Td ta="right">{amount(cashIn(m.ref, 'all'))}</Table.Td>
                        <Table.Td ta="right">
                          <Button size="xs" onClick={() => { const key = askBuyIn!; setAskBuyIn(null); void choosePick(key, m.ref); }}>Buy in {m.label}</Button>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
              {/* 4c (Pedro 2026-10-30, "Both"): or share it over the portfolios by the cash each has. */}
              <Group justify="space-between">
                <Tooltip withArrow multiline w={340} label="The buy is shared over the portfolios pro rata to the cash each has after the other trades and transfers (all its cash accounts), in whole lots — so it does not overdraw one while the others hold the cash. Its price is looked up for the first portfolio.">
                  <Button variant="light" onClick={() => { const key = askBuyIn!; setAskBuyIn(null); void choosePick(key, CASH_PICK); }}>
                    Split by available cash
                  </Button>
                </Tooltip>
                <Tooltip withArrow multiline w={340} label="Step 2's optimizer chooses the portfolio(s) it is bought in, by the consolidation's trade rules. Step 2 must then be Optimize further.">
                  <Button variant="light" color="grape" onClick={() => { const key = askBuyIn!; setAskBuyIn(null); void choosePick(key, OPT_PICK); }}>
                    Let the optimizer choose
                  </Button>
                </Tooltip>
                <Button variant="default" onClick={() => setAskBuyIn(null)}>Choose later (the row stays red)</Button>
              </Group>
            </Stack>
          </Modal>
        );
      })()}
      <Modal opened={confirmClear} onClose={() => setConfirmClear(false)} title="Clear all trades">
        <Stack>
          <Text size="sm">Clear every trade on screen, including the securities added to buy and every split typed by hand? The saved run (if any) is unchanged until you save again.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmClear(false)}>Cancel</Button>
            <Button color="red" onClick={() => { setEntries({}); setManual({}); setPick({}); setAdded([]); setTransfers([]);
              setOptKeys(new Set()); setOptXfers([]); setOptUsed(null); resetGuide(); touch(); setConfirmClear(false); }}>Clear all trades</Button>
          </Group>
        </Stack>
      </Modal>
      {/* S4-Q4's "Continue saving / Stay and correct" warning is replaced by step 3 of the run's steps
          (TC-S5-GUIDE G3): an overdraft is fixed or explicitly accepted there, before Save is enabled. */}
      {/* 4c: the run's cash transfers. A transfer between two portfolios is confirmed loudly (S4-Q9). */}
      {/* Smoke 2026-10-30 ("the window needs to be wider"): room for From, To, Out and In. */}
      {consolidation && (
        <OptimizeWindow opened={optOpen} onClose={() => setOptOpen(false)} consolidationId={consolidation.id}
          members={members.map((m) => ({ ref: m.ref, label: m.label, ccy: m.ccy, bank: m.bank }))} refCcy={refCcy}
          build={buildOptimize} rowName={(key) => rows.find((r) => r.key === key)?.name ?? key}
          onApply={applyOptimized} again={optKeys.size > 0}
          note={modelCash ? `The model ${modelCash.label} also sets cash per currency. The optimizer does not aim at it yet: it keeps each portfolio's minimum cash and moves cash between portfolios only as the trades need. Then, in the same transfer window, each portfolio's own accounts are brought to the model's cash per currency (and none below zero).` : null} />
      )}
      <Modal opened={xferOpen} onClose={() => setXferOpen(false)} size={1180} title="Cash transfers in this run">
        <Stack>
          {/* 5c smoke (Pedro 2026-11-01): opened by Rebalance to a model, say what the choice is. */}
          {xferFromRebalance && (
            // Pedro 2026-11-01: the light box "did not show up clearly on my screen" — filled, with an icon,
            // as the red box below it. TC-S5-FLOW: reached by "Keep this plan" on the rebalance note.
            <Alert color="blue" variant="filled" py={8} icon={<IconInfoCircle size={20} />}
              title={<Text fw={700} size="sm">The cash transfers of the plan you kept</Text>}>
              <Text size="sm" c="white">
                These transfers are part of the plan Rebalance to… made: they bring each currency's cash to the model's. Check
                them (change any if needed) and <b>Apply transfers</b>. Changed your mind? <b>Cancel</b>, then <b>Optimize further
                (according to trade rules)</b> on the trade bar's Optimize split….
              </Text>
            </Alert>
          )}
          <Text size="sm">
            Cash moved from one cash account to another: of the same portfolio, or of another portfolio of consolidation {name}
            (cash only). <b>Out</b> leaves the From account in its currency; <b>In</b> reaches the To account in its currency, at the
            sending custodian's rate when the currencies differ (an FX trade). Each transfer is saved with the draft of the portfolio
            the cash leaves. Two transfers between the same accounts in the same direction join into one.
          </Text>
          <Table withTableBorder fz="sm">
            <Table.Thead>
              <Table.Tr><Table.Th>From</Table.Th><Table.Th>To</Table.Th><Table.Th>Out</Table.Th><Table.Th>In</Table.Th><Table.Th w={40} /></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {xferRows.length === 0 && (
                <Table.Tr><Table.Td colSpan={5}><Text size="sm" c="dimmed">No transfers. Apply to save that, or add one.</Text></Table.Td></Table.Tr>
              )}
              {xferRows.map((r, i) => {
                const inn = innOf(r);
                const f = r.from ? splitAcc(r.from) : null, t = r.to ? splitAcc(r.to) : null;
                return (
                  <Table.Tr key={i}>
                    <Table.Td><Select size="xs" w={260} data={accOptions} value={r.from} comboboxProps={{ withinPortal: true }} placeholder="Sending account"
                      onChange={(v) => setXferRows((p) => p.map((x, j) => (j === i ? { ...x, from: v, ...(v && v === x.to ? { to: null } : {}) } : x)))} /></Table.Td>
                    <Table.Td><Select size="xs" w={260} data={accOptions.filter((o) => o.value !== r.from)} value={r.to} comboboxProps={{ withinPortal: true }}
                      placeholder="Receiving account" onChange={(v) => setXferRows((p) => p.map((x, j) => (j === i ? { ...x, to: v } : x)))} /></Table.Td>
                    <Table.Td><TextInput size="xs" w={130} value={r.out} rightSection={<Text size="xs" c="dimmed">{f ? accCcy(f.p, f.code) : ''}</Text>}
                      onChange={(e) => { const v = e.currentTarget.value; setXferRows((p) => p.map((x, j) => (j === i ? { ...x, out: v } : x))); }} /></Table.Td>
                    <Table.Td>
                      <TextInput size="xs" w={130} readOnly variant="filled" value={inn == null ? '' : String(inn)} aria-label={`Amount in, transfer ${i + 1}`}
                        rightSection={<Text size="xs" c="dimmed">{t ? accCcy(t.p, t.code) : ''}</Text>} />
                    </Table.Td>
                    <Table.Td>
                      <Tooltip withArrow label="Remove this transfer">
                        <ActionIcon variant="subtle" color="red" aria-label={`Remove transfer ${i + 1}`}
                          onClick={() => setXferRows((p) => p.filter((_x, j) => j !== i))}><IconTrash size={14} /></ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          <Group gap="xs">
            <Button size="xs" variant="light" onClick={() => setXferRows((p) => [...p, { from: null, to: null, out: '' }])}>+ Add a transfer</Button>
            <Tooltip withArrow multiline w={300} label="Adds transfers covering what the trades (and the transfers above) leave overdrawn: from the same portfolio first, then from the consolidation's other portfolios.">
              <Button size="xs" variant="light" onClick={() => setXferRows((p) => [...p, ...suggest(xferList)])}>Suggest for overdrafts</Button>
            </Tooltip>
            <Tooltip withArrow disabled={xferRows.length > 0} label="No transfers to remove.">
              <span><Button size="xs" variant="subtle" color="red" disabled={xferRows.length === 0} onClick={() => setXferRows([])}>Remove all</Button></span>
            </Tooltip>
          </Group>
          <Text size="sm" fw={500}>Cash after the trades and these transfers ({refCcy})</Text>
          <Group gap="md">
            {cash.map((a) => {
              const k = pk(a.p, a.code);
              const v = projected(k, xferList);
              const touched = xferList.some((t) => pk(t.fromP, t.from) === k || pk(t.toP, t.to) === k);
              if (!touched && !(v != null && v < -0.005)) return null;
              return <Text key={k} size="sm" c={v != null && v < -0.005 ? 'red' : undefined}>{accLabel(k)}: <b>{amount(v)} {refCcy}</b></Text>;
            })}
          </Group>
          {xferProblems.length > 0 && <Alert color="orange" py={4}><Stack gap={0}>{xferProblems.map((p) => <Text key={p} size="xs">{p}</Text>)}</Stack></Alert>}
          {xferCrossing.length > 0 && (
            <Alert color="red" variant="filled" py={6} icon={<IconAlertTriangle size={18} />}
              title={`${xferCrossing.length} transfer${xferCrossing.length === 1 ? '' : 's'} between portfolios: check before applying`}>
              <Stack gap={2}>
                {xferCrossing.map((t, i) => (
                  <Text key={i} size="xs">
                    {labelOf(t.fromP)} → {labelOf(t.toP)}: {amount(t.out)} {accCcy(t.fromP, t.from)}{crossesBanks(t) ? ' — crosses banks' : ''}.
                  </Text>
                ))}
                <Text size="xs">Cash leaves one portfolio of the client for another. Check that neither sits in an insurance policy (or other wrapper) the other is not in.</Text>
                {/* Smoke 2026-10-30: a white tick on a white box could not be seen — dark when ticked. */}
                <Checkbox size="sm" color="dark" checked={xferChecked} onChange={(e) => setXferChecked(e.currentTarget.checked)}
                  label={<Text size="xs" fw={600} c="white">I checked: these transfers between portfolios are allowed.</Text>} />
              </Stack>
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setXferOpen(false)}>Cancel</Button>
            <Tooltip withArrow multiline w={280} disabled={xferProblems.length === 0 && (xferCrossing.length === 0 || xferChecked)}
              label={xferProblems.length ? 'Fix what is listed above first.' : 'Tick "I checked" in the red box first.'}>
              <span><Button onClick={applyTransfers} disabled={xferProblems.length > 0 || (xferCrossing.length > 0 && !xferChecked)}>Apply transfers</Button></span>
            </Tooltip>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete run">
        <Stack>
          <Text size="sm">Delete <b>{lastSaved?.serial}{title ? ` · ${title}` : ''}</b> and its {lastSaved?.drafts.length ?? 0} drafts ({lastSaved?.drafts.map((d) => d.serial).join(', ')})? The portfolios are unchanged.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button color="red" onClick={() => void remove()}>Delete run</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );

  return { enabled, active, button, panel, width, headCells, rowCells, blankCells, footRows,
    addedRows: active ? addedRows : [], searchCell, onlyTraded: active && onlyTraded, isTraded,
    rowShade: (r: ConsolidatedRow) => (!active ? null : blockedKeys.has(r.key) ? 'var(--mantine-color-red-2)'
      : isTraded(r) ? 'var(--mantine-color-orange-0)' : null),
    subRows };
}
